import { startLoginAttempt, isLoginServiceError } from './loginAttempt'
import { staffSupportEnabled, hasSupportDestination, isSupportAccount, supportLoginPath, SUPPORT_ACCOUNT_EMAIL } from './staffSupportRoute'
import { startCompanyDataSync } from './companyDataSync'
import { loadActivePlan } from './entitlements'
import { readSupportAccess } from './supportApi'
import { SUPPORT_NOTIFICATION_TYPE, memberSupportEnabled, supportThreadIdFromNotification, createSupportNotificationRouter } from './supportNotifications'
import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, Activity } from 'react'
import { supabase, SUPABASE_ANON_KEY, SUPABASE_URL, getPasswordRecoveryProof, clearPasswordRecoveryProof, getSupportActivationProof, consumeSupportActivationProof } from './supabaseClient'
import HomeScreen from './HomeScreen'
import {FORUM_NOTIFICATION_TYPES,forumEnabled,forumInitialTarget,preserveForumTarget} from './forumApi'
import { getUiText } from './i18n'
import { geocodeSwissAddress } from './geo'
import { getPlatform, isNativeApp } from './platform'
import { notifyTelegramActivity } from './telegramAlerts'
import { HubbingIcon } from './icons'
import LandingNetwork from './LandingNetwork'
import './LandingMotion.css'
import { VerifiedBadge } from './VerifiedBadge'
import { clearAppBadge, showNativeNotification, syncUnreadAppBadge } from './appBadge'
import { registerPushNotifications } from './pushNotifications'
import { fetchPendingNeedCompletionCount, NEED_COMPLETION_NOTIFICATION_TYPE } from './needCompletions'
import { NEED_OPPORTUNITY_NOTIFICATION_TYPE } from './needOpportunityNotifications'
import { needShareNotificationText } from './needShareNotification'
import UsageGuideModal from './UsageGuideModal'
import LoadingIndicator from './LoadingIndicator'
import { ServiceTagsEditor } from './ServiceTagsComponents'
import { parseServiceTags } from './serviceTags'
import CompanyLookup from './CompanyLookup'
import DecisionMakerFields from './DecisionMakerFields'
import RegistrationEmailChoice from './RegistrationEmailChoice'
import { isPersonalEmail } from './postalVerification'
import { getPostalVerificationCopy } from './postalVerificationCopy'
import { getCompanyLookupCopy } from './companyLookupCopy'
import { lookupZefix } from './zefixLookup'
import { COMPANY_SECTORS } from './companySectors'
import { BILLING_PERIODS, subscriptionOffersEnabled } from './billingCatalog'
import { getBillingCopy } from './billingCopy'
import { companyIdFromSearch } from './companyLinks'
import { scheduleNavigationPreload } from './navigationPreload'
import { useNavigationMotion } from './useNavigationMotion'
import useConversationViewport from './useConversationViewport'
import useForumScrollClearance from './useForumScrollClearance'
import { createCompanySnapshotStore, getCompanySnapshotStorage, clearCompanySnapshotStorage } from './companySnapshotStore'

const navigationScreenLoaders = {
  swipe: () => import('./SwipeScreen'),
  map: () => import('./MapScreen'),
  messages: () => import('./MessagesScreen'),
  profile: () => import('./ProfileScreen'),
}
const SwipeScreen = React.lazy(navigationScreenLoaders.swipe)
const ProfileScreen = React.lazy(navigationScreenLoaders.profile)
const MessagesScreen = React.lazy(navigationScreenLoaders.messages)
const PricingScreen = React.lazy(() => import('./PricingScreen'))
const RegistrationOffers = React.lazy(() => import('./RegistrationOffers'))
const HubbingIntro = React.lazy(() => import('./HubbingIntro'))
const LegalScreen = React.lazy(() => import('./LegalScreen'))
const AdminScreen = React.lazy(() => import('./AdminScreen'))
const StaffSupportScreen = React.lazy(() => import('./StaffSupportScreen'))
const SupportObserverMessages = React.lazy(() => import('./SupportObserverMessages'))
const SupportActivationScreen = React.lazy(() => import('./SupportActivationScreen'))
const CompanyProfileScreen = React.lazy(() => import('./CompanyProfileScreen'))
const PrivacyPolicy = React.lazy(() => import('./PrivacyPolicy'))
const MapScreen = React.lazy(navigationScreenLoaders.map)
const APP_STORE_URL = 'https://apps.apple.com/ch/app/hubbing/id6762903411'
const ANDROID_PLAY_URL = 'https://play.google.com/store/apps/details?id=ch.hubbing.app'
const APP_VERSION = process.env.REACT_APP_VERSION_NAME || '1.0.10'
const APP_BUILD_NUMBER = Number(process.env.REACT_APP_VERSION_BUILD) || 87
const APP_VERSION_CONFIG_URL = 'https://app.hubbing.ch/app-version.json'
const RELEASE_ANNOUNCEMENT_STORAGE_PREFIX = 'hubbing_release_announcement_seen_'
const TERMS_OF_USE_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/'
const PRIVACY_POLICY_URL = 'https://app.hubbing.ch/privacy.html'
const SESSION_RESTORE_TIMEOUT_MS = 8000
const SESSION_IDLE_LIMIT_MS = 30 * 60 * 1000
const SESSION_LOCK_TTL_MINUTES = 35
const ENFORCE_SINGLE_DEVICE_LOCK = false
const ENABLE_INACTIVITY_SIGNOUT = false
const PUBLIC_SCREENS = ['home', 'login', 'register', 'legal', 'privacy', 'forgot-password', 'reset-password', 'verification-result']
const URL_SCREEN_SCREENS = ['login', 'register', 'legal', 'privacy', 'forgot-password', 'reset-password', 'verification-result']
const sessionTokenFallbacks = new Map()
const REGISTRATION_DRAFT_KEY = 'hubbing_registration_draft'
const NotificationCenter = React.lazy(() => import('./NotificationCenter'))
const ForumScreen = React.lazy(() => import('./ForumScreen'))

const MESSAGE_NOTIFICATION_TYPES = ['new_message', 'new_match']
const APP_NOTIFICATION_TYPES = ['new_company', ...MESSAGE_NOTIFICATION_TYPES, NEED_COMPLETION_NOTIFICATION_TYPE, NEED_OPPORTUNITY_NOTIFICATION_TYPE, 'need_shared']

const parseVersionParts = (version) => (
  String(version || '0')
    .split(/[^\d]+/)
    .filter(Boolean)
    .map(part => Number.parseInt(part, 10) || 0)
)

const compareVersions = (left, right) => {
  const leftParts = parseVersionParts(left)
  const rightParts = parseVersionParts(right)
  const length = Math.max(leftParts.length, rightParts.length)

  for (let index = 0; index < length; index += 1) {
    const leftValue = leftParts[index] || 0
    const rightValue = rightParts[index] || 0
    if (leftValue > rightValue) return 1
    if (leftValue < rightValue) return -1
  }

  return 0
}

const shouldRequireUpdate = (platformConfig) => {
  const minimumVersion = platformConfig?.minimum_supported_version || platformConfig?.min_version
  const minimumBuild = Number(platformConfig?.minimum_supported_build || platformConfig?.min_build || 0)

  if (minimumVersion && compareVersions(APP_VERSION, minimumVersion) < 0) return true
  if (minimumVersion && compareVersions(APP_VERSION, minimumVersion) === 0 && minimumBuild && APP_BUILD_NUMBER < minimumBuild) return true
  if (!minimumVersion && minimumBuild && APP_BUILD_NUMBER < minimumBuild) return true

  return false
}

const getDefaultStoreUrl = (platform) => (
  platform === 'android' ? ANDROID_PLAY_URL : APP_STORE_URL
)

const getRequiredUpdate = (config, platform) => {
  const platformConfig = config?.[platform]
  if (!platformConfig || !shouldRequireUpdate(platformConfig)) return null

  return {
    title: platformConfig.title || 'Mise à jour nécessaire',
    message: platformConfig.message || "Une nouvelle version de Hubbing est disponible. Veuillez mettre à jour l'application pour continuer.",
    buttonLabel: platformConfig.button_label || 'Mettre à jour',
    storeUrl: platformConfig.store_url || getDefaultStoreUrl(platform),
  }
}

const getReleaseAnnouncement = (config, platform, lang) => {
  const platformConfig = config?.[platform]
  const announcement = platformConfig?.release_announcement
  if (!announcement?.enabled) return null

  const releaseVersion = announcement.version || platformConfig?.latest_version
  if (releaseVersion && compareVersions(APP_VERSION, releaseVersion) < 0) return null

  const releaseId = String(announcement.id || `${platform}-${releaseVersion || APP_VERSION}`)
  const storageKey = `${RELEASE_ANNOUNCEMENT_STORAGE_PREFIX}${releaseId}`
  try {
    if (window.localStorage.getItem(storageKey) === '1') return null
  } catch {}

  const copy = announcement.copy?.[lang] || announcement.copy?.fr || announcement
  return {
    id: releaseId,
    storageKey,
    title: copy.title || 'Hubbing évolue',
    message: copy.message || 'Découvrez les nouveautés de cette mise à jour.',
    bullets: Array.isArray(copy.bullets) ? copy.bullets.map(item => String(item)).filter(Boolean) : [],
    buttonLabel: copy.button_label || copy.buttonLabel || 'Découvrir',
    version: releaseVersion || APP_VERSION,
  }
}

const signOutCurrentBrowser = () => supabase.auth.signOut({ scope: 'local' })

const clearHubbingStorage = (storage) => {
  if (!storage) return
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index)
      const normalizedKey = String(key || '').toLowerCase()
      if (
        normalizedKey.startsWith('sb-') ||
        normalizedKey.startsWith('hubbing_') ||
        normalizedKey.includes('supabase')
      ) {
        storage.removeItem(key)
      }
    }
  } catch (error) {
    console.warn('Unable to clear local storage:', error)
  }
}

const repairLocalBrowserSession = async () => {
  try {
    await signOutCurrentBrowser()
  } catch (error) {
    console.warn('Unable to sign out local browser session:', error)
  }

  clearHubbingStorage(window.localStorage)
  clearHubbingStorage(window.sessionStorage)

  if (window.caches?.keys) {
    try {
      const cacheNames = await window.caches.keys()
      await Promise.all(cacheNames.map(cacheName => window.caches.delete(cacheName)))
    } catch (error) {
      console.warn('Unable to clear browser caches:', error)
    }
  }

  if (window.navigator?.serviceWorker?.getRegistrations) {
    try {
      const registrations = await window.navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(registration => registration.unregister()))
    } catch (error) {
      console.warn('Unable to unregister service workers:', error)
    }
  }
}

const readRegistrationDraft = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(REGISTRATION_DRAFT_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

const clearRegistrationDraft = () => {
  try {
    window.sessionStorage.removeItem(REGISTRATION_DRAFT_KEY)
  } catch {
    // Registration still works when sessionStorage is unavailable.
  }
}

const getPasswordResetRedirectUrl = () => {
  const url = new URL(window.location.origin + window.location.pathname)
  url.searchParams.set('screen', 'reset-password')
  return url.toString()
}

const getStoredSessionToken = (userId) => {
  const key = `hubbing_session_token_${userId}`
  try {
    const existing = window.localStorage.getItem(key)
    if (existing) return existing
  } catch (error) {
    console.warn('Session token storage unavailable:', error)
  }

  const fallbackToken = sessionTokenFallbacks.get(userId)
  if (fallbackToken) return fallbackToken

  const token = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  sessionTokenFallbacks.set(userId, token)
  try {
    window.localStorage.setItem(key, token)
  } catch (error) {
    console.warn('Unable to persist session token:', error)
  }
  return token
}

const getDeviceLabel = () => {
  const ua = window.navigator?.userAgent || ''
  const surface = isNativeApp() ? 'App iOS' : 'Web'
  if (/iPad/i.test(ua)) return `${surface} iPad`
  if (/iPhone/i.test(ua)) return `${surface} iPhone`
  if (/Macintosh|Mac OS/i.test(ua)) return `${surface} Mac`
  return surface
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700&display=swap');

  @keyframes pulse { 
    0%, 100% { opacity:1; transform:scale(1); } 
    50% { opacity:0.5; transform:scale(0.8); } 
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  html, body, #root {
    width: 100%;
    height: 100%;
    overflow: hidden;
    -webkit-text-size-adjust: 100%;
  }

  body {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: #f5f5f5;
    overscroll-behavior: none;
  }

  .app {
    width: 100%;
    max-width: 430px;
    height: 100dvh;
    min-height: 100dvh;
    margin: 0 auto;
    background: white;
    position: relative;
    overflow: hidden;
  }

  .hb-landing > * { flex-shrink: 0; }

  input, textarea, select {
    font-size: 16px !important;
  }
`

const translations = {
  fr: {
    appTagline: 'Le réseau B2B pour les entreprises suisses',
    needsNotificationTagline: 'Recevez des notifications lorsqu’une entreprise publie un besoin qui correspond à vos services.',
    createAccount: 'Créer un compte',
    login: 'Se connecter',
    visitorMode: 'Continuer en mode visiteur',
    usageGuide: "Mode d'emploi",
    usageGuideTitle: "Mode d'emploi Hubbing",
    usageGuideIntro: 'Un repère rapide pour comprendre les menus avant de créer votre compte.',
    usageGuideClose: "Fermer le mode d'emploi",
    usageGuideSteps: [
      { title: 'Accueil', body: 'Retrouvez votre résumé, votre profil et les prochaines actions importantes.' },
      { title: 'Swipe', body: 'Parcourez les entreprises, filtrez par secteur, canton, rayon ou besoin, puis matchez les bons partenaires.' },
      { title: 'Carte', body: 'Visualisez les entreprises suisses autour de vous et ouvrez leur profil depuis la carte.' },
      { title: 'Messages', body: 'Échangez avec vos connexions, suivez les messages lus et gardez vos conversations organisées.' },
      { title: 'Profil', body: 'Mettez à jour vos informations, vos besoins B2B, vos visuels et vos préférences.' },
      { title: 'Tarifs', body: 'Comparez Starter, Basic et Premium pour choisir le niveau adapté à votre visibilité.' },
    ],
    planPreviewCta: 'Voir les tarifs',
    planPreviews: [
      { id: 'starter', name: 'Starter', price: 'Gratuit', detail: 'Découvrir' },
      { id: 'basic', name: 'Basic', price: 'CHF 19.-/mois', detail: 'Messages illimités' },
      { id: 'premium', name: 'Premium', price: 'CHF 39.-/mois', detail: 'Coordonnées complètes' },
    ],
    legal: 'CGU · Confidentialité · Mentions légales',
    registerTitle: 'Créer un compte',
    registerSubtitle: 'Réservé aux entreprises enregistrées en Suisse',
    companyInfo: 'INFORMATIONS ENTREPRISE',
    companyName: "Nom de l'entreprise *",
    sector: "Secteur d'activité *",
    ideNumber: 'Numéro IDE (CHE-xxx.xxx.xxx) *',
    companyAddress: "Adresse de l'entreprise *",
    contactInfo: 'DÉCIDEUR / CONTACT PRINCIPAL',
    contactName: 'Nom et prénom du décideur *',
    contactTitle: 'Titre du poste (CEO, Directeur...) *',
    contactPhone: 'Téléphone direct du décideur *',
    access: 'ACCÈS',
    email: 'Email professionnel *',
    password: 'Mot de passe (min. 6 caractères) *',
    showPassword: 'Afficher',
    hidePassword: 'Masquer',
    acceptCGU: "J'accepte les",
    cgu: "Conditions Générales d'Utilisation",
    and: 'et la',
    privacy: 'Politique de Confidentialité',
    of: 'de Hubbing.',
    creating: 'Création...',
    createBtn: 'Créer mon compte',
    loginTitle: 'Se connecter',
    back: '← Retour',
    noAccount: "Pas de compte ?",
    register: "S'inscrire",
    connecting: 'Connexion...',
    loginServiceUnavailable: 'Le service de connexion ne répond pas pour le moment. Réessayez dans quelques instants.',
    loginSessionMissing: 'La session n’a pas pu être enregistrée sur cet appareil. Vérifiez les données de site puis réessayez.',
    loginAttemptPending: 'La tentative précédente est encore en cours. Vous pourrez réessayer dès qu’elle sera terminée.',
    loginWaiting: 'En attente du service…',
    loginUnexpectedError: 'La connexion a échoué. Veuillez réessayer.',
    loginBtn: 'Se connecter',
    forgotPassword: 'Mot de passe oublié ?',
    repairLogin: 'Réparer la connexion sur cet appareil',
    repairingLogin: 'Nettoyage...',
    resetPasswordTitle: 'Réinitialiser le mot de passe',
    resetPasswordIntro: 'Entrez votre email professionnel. Si un compte existe, vous recevrez un lien sécurisé pour choisir un nouveau mot de passe.',
    resetPasswordEmailSent: 'Si un compte existe pour cette adresse, un email de réinitialisation vient d’être envoyé. Vérifiez aussi vos spams.',
    sendResetLink: 'Envoyer le lien',
    sendingResetLink: 'Envoi...',
    backToLogin: 'Retour à la connexion',
    newPasswordTitle: 'Nouveau mot de passe',
    newPasswordIntro: 'Choisissez un nouveau mot de passe pour sécuriser votre compte Hubbing.',
    newPassword: 'Nouveau mot de passe (min. 8 caractères)',
    confirmPassword: 'Confirmer le mot de passe',
    passwordMismatch: 'Les mots de passe ne correspondent pas.',
    passwordTooShort: 'Le mot de passe doit contenir au moins 8 caractères.',
    updatePassword: 'Mettre à jour le mot de passe',
    updatingPassword: 'Mise à jour...',
    passwordUpdated: 'Mot de passe mis à jour. Vous pouvez maintenant vous connecter.',
    resetLinkInvalid: 'Lien de réinitialisation invalide ou expiré. Demandez un nouveau lien.',
    resetSupportInviteInvalid: "Ce lien d’invitation ne permet pas de modifier le mot de passe ici. Demandez un nouveau lien d’activation du compte support.",
    resetAccountLabel: "Compte à réinitialiser :",
    resetContinue: 'Continuer',
    successTitle: 'Compte créé !',
    successMsg: 'Un email de confirmation vous a été envoyé. Cliquez sur le lien pour activer votre compte.',
    successBtn: 'Se connecter',
    errorFields: 'Veuillez remplir tous les champs obligatoires',
    errorCGU: 'Veuillez accepter les CGU pour continuer',
    errorEmail: 'Veuillez utiliser votre email professionnel (ex: vous@votreentreprise.ch)',
    errorEmailAlreadyUsed: 'Cet email est déjà utilisé. Connectez-vous ou utilisez la réinitialisation du mot de passe.',
    errorZefix: 'Numéro IDE introuvable. Vérifiez que votre entreprise est enregistrée en Suisse.',
    errorIdeAlreadyUsed: 'Ce numéro IDE est déjà utilisé sur Hubbing. Si cette entreprise vous appartient, connectez-vous ou contactez Hubbing.',
    errorZefixNotFound: 'Entreprise non trouvée dans le registre suisse (Zefix).',
    errorZefixInactive: "Cette entreprise n'est pas active dans Zefix. Contactez Hubbing si c'est une erreur.",
    errorZefixMismatch: "Les informations saisies ne correspondent pas aux données Zefix. Utilisez le nom et l'adresse officiels de l'entreprise ou contactez Hubbing.",
    errorEmailCompanyMismatch: "L'email professionnel ne semble pas correspondre à l'entreprise indiquée dans Zefix. Utilisez une adresse email de l'entreprise ou contactez Hubbing.",
    errorZefixRetry: 'Impossible de vérifier le numéro IDE. Réessayez.',
    zefixValidFormat: "Numéro IDE au bon format — vérification Zefix au moment de l'inscription",
    zefixChecking: 'Vérification Zefix en cours...',
    zefixVerified: (name) => `Entreprise vérifiée via Zefix${name ? ` : ${name}` : ''}`,
    zefixManualFallback: 'Vérification Zefix temporairement indisponible — contrôle manuel sous 24 à 48h ouvrables',
    sessionAlreadyOpen: 'Votre compte est déjà ouvert sur un autre appareil. Déconnectez-vous de cet appareil ou réessayez dans 30 minutes.',
    sessionExpired: 'Vous avez été déconnecté après 30 minutes d’inactivité.',
    notificationNewMessageTitle: 'Nouveau message Hubbing',
    notificationNewMessageBody: 'Vous avez reçu un nouveau message.',
    notificationNewMatchTitle: 'Nouveau match Hubbing',
    notificationNewMatchBody: 'Une entreprise vient de matcher avec vous.',
    notificationNeedCompletionTitle: 'Réalisation à confirmer',
    notificationNeedCompletionBody: 'Une entreprise indique que vous avez réalisé un besoin.',
    notificationNeedOpportunityTitle: 'Nouvelle opportunité B2B',
    notificationNeedOpportunityBody: 'Une entreprise recherche un service que vous proposez.',
    demoMode: 'Mode visiteur',
    demoSwipe: 'Mode visiteur — connectez-vous pour matcher',
    demoMap: 'Mode visiteur — connectez-vous pour voir toutes les entreprises',
    companies: 'entreprises enregistrées en Suisse',
    mapTitle: 'Carte des entreprises',
    seeMap: 'Voir la carte complète',
    messagesTitle: 'Messagerie B2B',
    messagesDesc: 'Échangez directement avec vos connexions business',
    accessMessages: 'Accéder à la messagerie',
    pricing: 'Nos tarifs',
    pricingDesc: 'Développez votre réseau B2B en Suisse',
    lockTitle: 'Créez un compte gratuit',
    lockDesc: 'Rejoignez Hubbing pour accéder à toutes les fonctionnalités B2B.',
    createFree: 'Créer un compte gratuit',
    continueDemo: 'Continuer en mode démo',
    logout: 'Déconnexion',
    changeLanguage: 'Langue',
  },
  de: {
    needsNotificationTagline: 'Erhalten Sie Benachrichtigungen, wenn ein Unternehmen einen Bedarf veröffentlicht, der zu Ihren Dienstleistungen passt.',
    appTagline: 'Das B2B-Netzwerk für Schweizer Unternehmen',
    createAccount: 'Konto erstellen',
    login: 'Anmelden',
    visitorMode: 'Als Besucher fortfahren',
    usageGuide: 'Anleitung',
    usageGuideTitle: 'So nutzen Sie Hubbing',
    usageGuideIntro: 'Eine kurze Übersicht über die Menüs, bevor Sie ein Konto erstellen.',
    usageGuideClose: 'Anleitung schliessen',
    usageGuideSteps: [
      { title: 'Start', body: 'Sehen Sie Ihre Übersicht, Ihr Profil und die nächsten wichtigen Aktionen.' },
      { title: 'Swipe', body: 'Entdecken Sie Unternehmen, filtern Sie nach Branche, Kanton, Radius oder Bedarf und matchen Sie passende Partner.' },
      { title: 'Karte', body: 'Sehen Sie Schweizer Unternehmen auf der Karte und öffnen Sie deren Profil direkt.' },
      { title: 'Nachrichten', body: 'Tauschen Sie sich mit Ihren Kontakten aus und behalten Sie Unterhaltungen im Blick.' },
      { title: 'Profil', body: 'Aktualisieren Sie Unternehmensdaten, B2B-Bedarf, Bilder und Einstellungen.' },
      { title: 'Preise', body: 'Vergleichen Sie Starter, Basic und Premium für die passende Sichtbarkeit.' },
    ],
    planPreviewCta: 'Preise ansehen',
    planPreviews: [
      { id: 'starter', name: 'Starter', price: 'Gratis', detail: 'Entdecken' },
      { id: 'basic', name: 'Basic', price: 'CHF 19.-/Monat', detail: 'Unbegrenzte Nachrichten' },
      { id: 'premium', name: 'Premium', price: 'CHF 39.-/Monat', detail: 'Vollständige Kontakte' },
    ],
    legal: 'AGB · Datenschutz · Impressum',
    registerTitle: 'Konto erstellen',
    registerSubtitle: 'Nur für in der Schweiz registrierte Unternehmen',
    companyInfo: 'UNTERNEHMENSINFORMATIONEN',
    companyName: 'Unternehmensname *',
    sector: 'Branche *',
    ideNumber: 'UID-Nummer (CHE-xxx.xxx.xxx) *',
    companyAddress: 'Unternehmensadresse *',
    contactInfo: 'ENTSCHEIDUNGSTRÄGER / HAUPTKONTAKT',
    contactName: 'Vor- und Nachname *',
    contactTitle: 'Berufsbezeichnung (CEO, Direktor...) *',
    contactPhone: 'Direkte Telefonnummer *',
    access: 'ZUGANG',
    email: 'Geschäftliche E-Mail *',
    password: 'Passwort (mind. 6 Zeichen) *',
    showPassword: 'Anzeigen',
    hidePassword: 'Verbergen',
    acceptCGU: 'Ich akzeptiere die',
    cgu: 'Allgemeinen Geschäftsbedingungen',
    and: 'und die',
    privacy: 'Datenschutzrichtlinie',
    of: 'von Hubbing.',
    creating: 'Wird erstellt...',
    createBtn: 'Konto erstellen',
    loginTitle: 'Anmelden',
    back: '← Zurück',
    noAccount: 'Kein Konto?',
    register: 'Registrieren',
    connecting: 'Verbindung...',
    loginServiceUnavailable: 'Der Anmeldedienst antwortet derzeit nicht. Versuchen Sie es in Kürze erneut.',
    loginSessionMissing: 'Die Sitzung konnte auf diesem Gerät nicht gespeichert werden. Prüfen Sie die Website-Daten und versuchen Sie es erneut.',
    loginAttemptPending: 'Der vorherige Versuch läuft noch. Sie können es erneut versuchen, sobald er beendet ist.',
    loginWaiting: 'Warten auf den Dienst…',
    loginUnexpectedError: 'Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    loginBtn: 'Anmelden',
    forgotPassword: 'Passwort vergessen?',
    repairLogin: 'Anmeldung auf diesem Gerät reparieren',
    repairingLogin: 'Bereinigung...',
    resetPasswordTitle: 'Passwort zurücksetzen',
    resetPasswordIntro: 'Geben Sie Ihre geschäftliche E-Mail ein. Falls ein Konto existiert, erhalten Sie einen sicheren Link.',
    resetPasswordEmailSent: 'Falls ein Konto für diese Adresse existiert, wurde ein Link zum Zurücksetzen gesendet. Prüfen Sie auch den Spam-Ordner.',
    sendResetLink: 'Link senden',
    sendingResetLink: 'Wird gesendet...',
    backToLogin: 'Zurück zur Anmeldung',
    newPasswordTitle: 'Neues Passwort',
    newPasswordIntro: 'Wählen Sie ein neues Passwort für Ihr Hubbing-Konto.',
    newPassword: 'Neues Passwort (mind. 8 Zeichen)',
    confirmPassword: 'Passwort bestätigen',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
    passwordTooShort: 'Das Passwort muss mindestens 8 Zeichen enthalten.',
    updatePassword: 'Passwort aktualisieren',
    updatingPassword: 'Aktualisierung...',
    passwordUpdated: 'Passwort aktualisiert. Sie können sich jetzt anmelden.',
    resetLinkInvalid: 'Ungültiger oder abgelaufener Link. Fordern Sie einen neuen Link an.',
    resetSupportInviteInvalid: "Dieser Einladungslink kann hier kein Passwort ändern. Fordern Sie einen neuen Aktivierungslink für das Supportkonto an.",
    resetAccountLabel: "Konto zum Zurücksetzen:",
    resetContinue: 'Weiter',
    successTitle: 'Konto erstellt!',
    successMsg: 'Eine Bestätigungs-E-Mail wurde gesendet. Klicken Sie auf den Link, um Ihr Konto zu aktivieren.',
    successBtn: 'Anmelden',
    errorFields: 'Bitte füllen Sie alle Pflichtfelder aus',
    errorCGU: 'Bitte akzeptieren Sie die AGB',
    errorEmail: 'Bitte verwenden Sie Ihre geschäftliche E-Mail (z.B. sie@ihrfirma.ch)',
    errorEmailAlreadyUsed: 'Diese E-Mail wird bereits verwendet. Melden Sie sich an oder setzen Sie Ihr Passwort zurück.',
    errorZefix: 'UID-Nummer nicht gefunden. Prüfen Sie, ob Ihr Unternehmen in der Schweiz registriert ist.',
    errorIdeAlreadyUsed: 'Diese UID-Nummer wird bereits auf Hubbing verwendet. Melden Sie sich an oder kontaktieren Sie Hubbing.',
    errorZefixNotFound: 'Unternehmen nicht im Schweizer Register (Zefix) gefunden.',
    errorZefixInactive: 'Dieses Unternehmen ist in Zefix nicht aktiv. Kontaktieren Sie Hubbing, falls dies ein Fehler ist.',
    errorZefixMismatch: 'Die eingegebenen Angaben stimmen nicht mit den Zefix-Daten überein. Verwenden Sie den offiziellen Namen und die offizielle Adresse oder kontaktieren Sie Hubbing.',
    errorEmailCompanyMismatch: 'Die geschäftliche E-Mail scheint nicht zum in Zefix angegebenen Unternehmen zu passen. Verwenden Sie eine Firmen-E-Mail oder kontaktieren Sie Hubbing.',
    errorZefixRetry: 'UID-Nummer konnte nicht verifiziert werden. Versuchen Sie es erneut.',
    zefixValidFormat: 'UID-Nummer im richtigen Format — Zefix-Prüfung bei der Registrierung',
    zefixChecking: 'Zefix-Prüfung läuft...',
    zefixVerified: (name) => `Unternehmen via Zefix verifiziert${name ? `: ${name}` : ''}`,
    zefixManualFallback: 'Zefix-Prüfung vorübergehend nicht verfügbar — manuelle Prüfung innerhalb von 24 bis 48 Arbeitsstunden',
    sessionAlreadyOpen: 'Ihr Konto ist bereits auf einem anderen Gerät geöffnet. Melden Sie sich dort ab oder versuchen Sie es in 30 Minuten erneut.',
    sessionExpired: 'Sie wurden nach 30 Minuten Inaktivität abgemeldet.',
    notificationNewMessageTitle: 'Neue Hubbing-Nachricht',
    notificationNewMessageBody: 'Sie haben eine neue Nachricht erhalten.',
    notificationNewMatchTitle: 'Neues Hubbing-Match',
    notificationNewMatchBody: 'Ein Unternehmen hat gerade mit Ihnen gematcht.',
    notificationNeedCompletionTitle: 'Leistung bestätigen',
    notificationNeedCompletionBody: 'Ein Unternehmen gibt an, dass Sie einen Bedarf erfüllt haben.',
    notificationNeedOpportunityTitle: 'Neue B2B-Chance',
    notificationNeedOpportunityBody: 'Ein Unternehmen sucht eine Dienstleistung, die Sie anbieten.',
    demoMode: 'Besucher-Modus',
    demoSwipe: 'Besucher-Modus — anmelden zum Matchen',
    demoMap: 'Besucher-Modus — anmelden für alle Unternehmen',
    companies: 'registrierte Unternehmen in der Schweiz',
    mapTitle: 'Unternehmenskarte',
    seeMap: 'Vollständige Karte anzeigen',
    messagesTitle: 'B2B-Messaging',
    messagesDesc: 'Tauschen Sie sich direkt mit Ihren Business-Kontakten aus',
    accessMessages: 'Zum Messaging',
    pricing: 'Unsere Preise',
    pricingDesc: 'Entwickeln Sie Ihr B2B-Netzwerk in der Schweiz',
    lockTitle: 'Kostenloses Konto erstellen',
    lockDesc: 'Treten Sie Hubbing bei, um alle B2B-Funktionen zu nutzen.',
    createFree: 'Kostenloses Konto erstellen',
    continueDemo: 'Im Demo-Modus fortfahren',
    logout: 'Abmelden',
    changeLanguage: 'Sprache',
  },
  it: {
    needsNotificationTagline: 'Ricevete una notifica quando un’azienda pubblica un’esigenza che corrisponde ai vostri servizi.',
    appTagline: 'La rete B2B per le aziende svizzere',
    createAccount: 'Crea un account',
    login: 'Accedi',
    visitorMode: 'Continua in modalità visitatore',
    usageGuide: "Guida all'uso",
    usageGuideTitle: 'Come usare Hubbing',
    usageGuideIntro: 'Una panoramica rapida dei menu prima di creare il tuo account.',
    usageGuideClose: 'Chiudi la guida',
    usageGuideSteps: [
      { title: 'Home', body: 'Trova il riepilogo, il profilo e le prossime azioni importanti.' },
      { title: 'Swipe', body: 'Scopri aziende, filtra per settore, cantone, raggio o bisogno e crea match utili.' },
      { title: 'Mappa', body: 'Visualizza le aziende svizzere sulla mappa e apri il loro profilo.' },
      { title: 'Messaggi', body: 'Scambia messaggi con le connessioni e tieni ordinate le conversazioni.' },
      { title: 'Profilo', body: 'Aggiorna dati aziendali, bisogni B2B, immagini e preferenze.' },
      { title: 'Tariffe', body: 'Confronta Starter, Basic e Premium per scegliere la visibilità giusta.' },
    ],
    planPreviewCta: 'Vedi tariffe',
    planPreviews: [
      { id: 'starter', name: 'Starter', price: 'Gratis', detail: 'Scoprire' },
      { id: 'basic', name: 'Basic', price: 'CHF 19.-/mese', detail: 'Messaggi illimitati' },
      { id: 'premium', name: 'Premium', price: 'CHF 39.-/mese', detail: 'Contatti completi' },
    ],
    legal: 'CGU · Privacy · Note legali',
    registerTitle: 'Crea un account',
    registerSubtitle: 'Riservato alle aziende registrate in Svizzera',
    companyInfo: 'INFORMAZIONI AZIENDALI',
    companyName: "Nome dell'azienda *",
    sector: 'Settore di attività *',
    ideNumber: 'Numero IDE (CHE-xxx.xxx.xxx) *',
    companyAddress: "Indirizzo dell'azienda *",
    contactInfo: 'RESPONSABILE / CONTATTO PRINCIPALE',
    contactName: 'Nome e cognome *',
    contactTitle: 'Titolo (CEO, Direttore...) *',
    contactPhone: 'Telefono diretto *',
    access: 'ACCESSO',
    email: 'Email professionale *',
    password: 'Password (min. 6 caratteri) *',
    showPassword: 'Mostra',
    hidePassword: 'Nascondi',
    acceptCGU: 'Accetto i',
    cgu: 'Termini e Condizioni',
    and: 'e la',
    privacy: 'Politica sulla Privacy',
    of: 'di Hubbing.',
    creating: 'Creazione...',
    createBtn: 'Crea il mio account',
    loginTitle: 'Accedi',
    back: '← Indietro',
    noAccount: 'Nessun account?',
    register: 'Registrati',
    connecting: 'Connessione...',
    loginServiceUnavailable: 'Il servizio di accesso al momento non risponde. Riprova tra qualche istante.',
    loginSessionMissing: 'Impossibile salvare la sessione su questo dispositivo. Controlla i dati del sito e riprova.',
    loginAttemptPending: 'Il tentativo precedente è ancora in corso. Potrai riprovare non appena sarà terminato.',
    loginWaiting: 'In attesa del servizio…',
    loginUnexpectedError: 'Accesso non riuscito. Riprova.',
    loginBtn: 'Accedi',
    forgotPassword: 'Password dimenticata?',
    repairLogin: "Riparare l'accesso su questo dispositivo",
    repairingLogin: 'Pulizia...',
    resetPasswordTitle: 'Reimposta la password',
    resetPasswordIntro: "Inserisci l'email professionale. Se esiste un account, riceverai un link sicuro.",
    resetPasswordEmailSent: 'Se esiste un account per questo indirizzo, è stato inviato un link. Controlla anche lo spam.',
    sendResetLink: 'Invia il link',
    sendingResetLink: 'Invio...',
    backToLogin: "Torna all'accesso",
    newPasswordTitle: 'Nuova password',
    newPasswordIntro: 'Scegli una nuova password per il tuo account Hubbing.',
    newPassword: 'Nuova password (min. 8 caratteri)',
    confirmPassword: 'Conferma la password',
    passwordMismatch: 'Le password non corrispondono.',
    passwordTooShort: 'La password deve contenere almeno 8 caratteri.',
    updatePassword: 'Aggiorna password',
    updatingPassword: 'Aggiornamento...',
    passwordUpdated: 'Password aggiornata. Ora puoi accedere.',
    resetLinkInvalid: 'Link non valido o scaduto. Richiedi un nuovo link.',
    resetSupportInviteInvalid: "Questo link di invito non consente di modificare la password qui. Richiedi un nuovo link di attivazione per il conto supporto.",
    resetAccountLabel: "Account da reimpostare:",
    resetContinue: 'Continua',
    successTitle: 'Account creato!',
    successMsg: "Un'email di conferma è stata inviata. Clicca sul link per attivare il tuo account.",
    successBtn: 'Accedi',
    errorFields: 'Si prega di compilare tutti i campi obbligatori',
    errorCGU: 'Si prega di accettare i CGU per continuare',
    errorEmail: "Utilizzare l'email professionale (es: voi@vostraazienda.ch)",
    errorEmailAlreadyUsed: 'Questa email è già utilizzata. Accedi o reimposta la password.',
    errorZefix: 'Numero IDE non trovato. Verificare che la sua azienda sia registrata in Svizzera.',
    errorIdeAlreadyUsed: 'Questo numero IDE è già utilizzato su Hubbing. Accedi o contatta Hubbing.',
    errorZefixNotFound: 'Azienda non trovata nel registro svizzero (Zefix).',
    errorZefixInactive: 'Questa azienda non è attiva in Zefix. Contatta Hubbing se si tratta di un errore.',
    errorZefixMismatch: "Le informazioni inserite non corrispondono ai dati Zefix. Usa il nome e l'indirizzo ufficiali dell'azienda o contatta Hubbing.",
    errorEmailCompanyMismatch: "L'email professionale non sembra corrispondere all'azienda indicata in Zefix. Usa un indirizzo email aziendale o contatta Hubbing.",
    errorZefixRetry: 'Impossibile verificare il numero IDE. Riprovare.',
    zefixValidFormat: "Numero IDE nel formato corretto — verifica Zefix durante l'iscrizione",
    zefixChecking: 'Verifica Zefix in corso...',
    zefixVerified: (name) => `Azienda verificata tramite Zefix${name ? `: ${name}` : ''}`,
    zefixManualFallback: 'Verifica Zefix temporaneamente non disponibile — controllo manuale entro 24-48 ore lavorative',
    sessionAlreadyOpen: 'Il tuo account è già aperto su un altro dispositivo. Disconnettiti da quel dispositivo o riprova tra 30 minuti.',
    sessionExpired: 'Sei stato disconnesso dopo 30 minuti di inattività.',
    notificationNewMessageTitle: 'Nuovo messaggio Hubbing',
    notificationNewMessageBody: 'Hai ricevuto un nuovo messaggio.',
    notificationNewMatchTitle: 'Nuovo match Hubbing',
    notificationNewMatchBody: "Un'azienda ha appena fatto match con te.",
    notificationNeedCompletionTitle: 'Realizzazione da confermare',
    notificationNeedCompletionBody: "Un'azienda indica che hai realizzato un bisogno.",
    notificationNeedOpportunityTitle: 'Nuova opportunità B2B',
    notificationNeedOpportunityBody: "Un'azienda cerca un servizio che offri.",
    demoMode: 'Modalità visitatore',
    demoSwipe: 'Modalità visitatore — accedi per fare match',
    demoMap: 'Modalità visitatore — accedi per vedere tutte le aziende',
    companies: 'aziende registrate in Svizzera',
    mapTitle: 'Mappa delle aziende',
    seeMap: 'Vedi la mappa completa',
    messagesTitle: 'Messaggistica B2B',
    messagesDesc: 'Scambia messaggi direttamente con i tuoi contatti business',
    accessMessages: 'Accedi alla messaggistica',
    pricing: 'I nostri prezzi',
    pricingDesc: 'Sviluppa la tua rete B2B in Svizzera',
    lockTitle: 'Crea un account gratuito',
    lockDesc: 'Unisciti a Hubbing per accedere a tutte le funzionalità B2B.',
    createFree: 'Crea un account gratuito',
    continueDemo: 'Continua in modalità demo',
    logout: 'Disconnetti',
    changeLanguage: 'Lingua',
  },
  en: {
    needsNotificationTagline: 'Get notified when a company posts a need that matches your services.',
    appTagline: 'The B2B network for Swiss companies',
    createAccount: 'Create an account',
    login: 'Log in',
    visitorMode: 'Continue as visitor',
    usageGuide: 'User guide',
    usageGuideTitle: 'How to use Hubbing',
    usageGuideIntro: 'A quick overview of the menus before creating your account.',
    usageGuideClose: 'Close guide',
    usageGuideSteps: [
      { title: 'Home', body: 'Find your summary, profile status and the next important actions.' },
      { title: 'Swipe', body: 'Browse companies, filter by sector, canton, radius or need, then match useful partners.' },
      { title: 'Map', body: 'See Swiss companies on the map and open their profile directly.' },
      { title: 'Messages', body: 'Exchange messages with your connections and keep conversations organized.' },
      { title: 'Profile', body: 'Update company details, B2B needs, visuals and preferences.' },
      { title: 'Pricing', body: 'Compare Starter, Basic and Premium to choose the right visibility level.' },
    ],
    planPreviewCta: 'View pricing',
    planPreviews: [
      { id: 'starter', name: 'Starter', price: 'Free', detail: 'Discover' },
      { id: 'basic', name: 'Basic', price: 'CHF 19.-/month', detail: 'Unlimited messages' },
      { id: 'premium', name: 'Premium', price: 'CHF 39.-/month', detail: 'Full contact details' },
    ],
    legal: 'T&C · Privacy · Legal notice',
    registerTitle: 'Create an account',
    registerSubtitle: 'Reserved for companies registered in Switzerland',
    companyInfo: 'COMPANY INFORMATION',
    companyName: 'Company name *',
    sector: 'Business sector *',
    ideNumber: 'IDE number (CHE-xxx.xxx.xxx) *',
    companyAddress: 'Company address *',
    contactInfo: 'DECISION MAKER / MAIN CONTACT',
    contactName: 'Full name *',
    contactTitle: 'Job title (CEO, Director...) *',
    contactPhone: 'Direct phone number *',
    access: 'ACCESS',
    email: 'Professional email *',
    password: 'Password (min. 6 characters) *',
    showPassword: 'Show',
    hidePassword: 'Hide',
    acceptCGU: 'I accept the',
    cgu: 'Terms and Conditions',
    and: 'and the',
    privacy: 'Privacy Policy',
    of: 'of Hubbing.',
    creating: 'Creating...',
    createBtn: 'Create my account',
    loginTitle: 'Log in',
    back: '← Back',
    noAccount: 'No account?',
    register: 'Sign up',
    connecting: 'Connecting...',
    loginServiceUnavailable: 'The sign-in service is not responding right now. Please retry shortly.',
    loginSessionMissing: 'The session could not be saved on this device. Check the site data settings and retry.',
    loginAttemptPending: 'The previous attempt is still running. You can retry once it has finished.',
    loginWaiting: 'Waiting for the service…',
    loginUnexpectedError: 'Sign-in failed. Please retry.',
    loginBtn: 'Log in',
    forgotPassword: 'Forgot password?',
    repairLogin: 'Repair login on this device',
    repairingLogin: 'Cleaning...',
    resetPasswordTitle: 'Reset password',
    resetPasswordIntro: 'Enter your professional email. If an account exists, you will receive a secure link.',
    resetPasswordEmailSent: 'If an account exists for this address, a reset email has been sent. Please also check spam.',
    sendResetLink: 'Send link',
    sendingResetLink: 'Sending...',
    backToLogin: 'Back to login',
    newPasswordTitle: 'New password',
    newPasswordIntro: 'Choose a new password for your Hubbing account.',
    newPassword: 'New password (min. 8 characters)',
    confirmPassword: 'Confirm password',
    passwordMismatch: 'Passwords do not match.',
    passwordTooShort: 'Password must contain at least 8 characters.',
    updatePassword: 'Update password',
    updatingPassword: 'Updating...',
    passwordUpdated: 'Password updated. You can now log in.',
    resetLinkInvalid: 'Invalid or expired reset link. Request a new link.',
    resetSupportInviteInvalid: "This invitation link cannot change a password here. Request a new activation link for the support account.",
    resetAccountLabel: "Account to reset:",
    resetContinue: 'Continue',
    successTitle: 'Account created!',
    successMsg: 'A confirmation email has been sent. Click the link to activate your account.',
    successBtn: 'Log in',
    errorFields: 'Please fill in all required fields',
    errorCGU: 'Please accept the T&C to continue',
    errorEmail: 'Please use your professional email (e.g. you@yourcompany.ch)',
    errorEmailAlreadyUsed: 'This email is already used. Log in or reset your password.',
    errorZefix: 'IDE number not found. Check that your company is registered in Switzerland.',
    errorIdeAlreadyUsed: 'This IDE number is already used on Hubbing. Log in or contact Hubbing.',
    errorZefixNotFound: 'Company not found in the Swiss register (Zefix).',
    errorZefixInactive: 'This company is not active in Zefix. Contact Hubbing if this is an error.',
    errorZefixMismatch: 'The submitted details do not match the Zefix data. Use the official company name and address or contact Hubbing.',
    errorEmailCompanyMismatch: 'The professional email does not seem to match the company listed in Zefix. Use a company email address or contact Hubbing.',
    errorZefixRetry: 'Unable to verify IDE number. Please try again.',
    zefixValidFormat: 'IDE number format is valid — Zefix verification will run during registration',
    zefixChecking: 'Checking Zefix...',
    zefixVerified: (name) => `Company verified through Zefix${name ? `: ${name}` : ''}`,
    zefixManualFallback: 'Zefix verification temporarily unavailable — manual review within 24 to 48 business hours',
    sessionAlreadyOpen: 'Your account is already open on another device. Sign out there or try again in 30 minutes.',
    sessionExpired: 'You were signed out after 30 minutes of inactivity.',
    notificationNewMessageTitle: 'New Hubbing message',
    notificationNewMessageBody: 'You received a new message.',
    notificationNewMatchTitle: 'New Hubbing match',
    notificationNewMatchBody: 'A company just matched with you.',
    notificationNeedCompletionTitle: 'Work completion to confirm',
    notificationNeedCompletionBody: 'A company says you completed a need.',
    notificationNeedOpportunityTitle: 'New B2B opportunity',
    notificationNeedOpportunityBody: 'A company is looking for a service you offer.',
    demoMode: 'Visitor mode',
    demoSwipe: 'Visitor mode — log in to match',
    demoMap: 'Visitor mode — log in to see all companies',
    companies: 'companies registered in Switzerland',
    mapTitle: 'Company map',
    seeMap: 'See full map',
    messagesTitle: 'B2B Messaging',
    messagesDesc: 'Exchange messages directly with your business connections',
    accessMessages: 'Access messaging',
    pricing: 'Our pricing',
    pricingDesc: 'Grow your B2B network in Switzerland',
    lockTitle: 'Create a free account',
    lockDesc: 'Join Hubbing to access all B2B features.',
    createFree: 'Create a free account',
    continueDemo: 'Continue in demo mode',
    logout: 'Log out',
    changeLanguage: 'Language',
  }
}

export default function App() {
  const companyLinkRef = useRef(companyIdFromSearch(window.location.search))
  const consumeCompanyLink = useCallback(() => { companyLinkRef.current = null }, [])
  const [registrationSelection, setRegistrationSelection] = useState(null)
  const supportEnabled = staffSupportEnabled(window.location.hostname, isNativeApp())
  const supportDestinationRef = useRef(supportEnabled && hasSupportDestination(window.location.search))
  const [screen, setScreenState] = useState(() => {
    const requestedScreen = new URLSearchParams(window.location.search).get('screen')
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    if (hashParams.get('type') === 'recovery' || (supportEnabled && typeof getPasswordRecoveryProof==='function' && getPasswordRecoveryProof()?.kind==='recovery')) return 'reset-password'
    if (supportEnabled && ['support-activate','support-login','support-app'].includes(requestedScreen)) return requestedScreen
    if (requestedScreen === 'visitor') return 'register'
    if (requestedScreen === 'admin' && !isNativeApp()) return 'admin'
    if (requestedScreen === 'support' && supportEnabled) return 'support'
    if (requestedScreen === 'pricing') return 'login'
    if (forumEnabled()&&forumInitialTarget()) return 'login'
    if (companyLinkRef.current && !PUBLIC_SCREENS.includes(requestedScreen)) return 'login'
    return PUBLIC_SCREENS.includes(requestedScreen) ? requestedScreen : 'home'
  })
  const requestedTabRef = useRef(new URLSearchParams(window.location.search).get('screen') === 'pricing' ? 'pricing' : 'home')
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lang, setLang] = useState('fr')
  const postalSignupReturnTabRef = useRef(null)
  const [requiredUpdate, setRequiredUpdate] = useState(null)
  const [releaseAnnouncement, setReleaseAnnouncement] = useState(null)
  const hostname = window.location.hostname.toLowerCase()
  const isStandalonePwa = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator?.standalone === true
  const isMarketingSite = (hostname === 'hubbing.ch' || hostname === 'www.hubbing.ch') && !isStandalonePwa
  const t = translations[lang]
  const setScreen = (nextScreenOrUpdater) => {
    setScreenState(currentScreen => {
      let nextScreen = typeof nextScreenOrUpdater === 'function'
        ? nextScreenOrUpdater(currentScreen)
        : nextScreenOrUpdater
      if (nextScreen === 'visitor') nextScreen = 'register'
      if (['support','support-login','support-app'].includes(nextScreen) && supportEnabled) supportDestinationRef.current = true
      if (['home','app','register'].includes(nextScreen)) supportDestinationRef.current = false

      if (PUBLIC_SCREENS.includes(nextScreen) || (nextScreen==='admin'&&!isNativeApp()) || nextScreen==='app' || (['support','support-activate','support-login','support-app'].includes(nextScreen) && supportEnabled)) {
        const url = new URL(window.location.href)
        if (URL_SCREEN_SCREENS.includes(nextScreen) || nextScreen==='admin' || (['support','support-activate','support-login','support-app'].includes(nextScreen) && supportEnabled)) {
          url.searchParams.set('screen', nextScreen)
        } else {
          url.searchParams.delete('screen')
        }
        if (supportEnabled && supportDestinationRef.current && ['login','forgot-password','reset-password'].includes(nextScreen)) url.searchParams.set('next','support')
        else if (supportEnabled) url.searchParams.delete('next')
        if (nextScreen !== 'verification-result') {
          ;['tone', 'status', 'title', 'message'].forEach(param => url.searchParams.delete(param))
        }
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
      }

      return nextScreen
    })
  }

  const startRegistration = (plan = '') => {
    clearRegistrationDraft()
    setRegistrationSelection({ plan, period: 'monthly' })
    setScreen('register')
  }

  const returnHomeFromRegistration = () => {
    clearRegistrationDraft()
    setRegistrationSelection(null)
    setScreen('home')
  }

  useEffect(() => {
    if (isNativeApp()) return
    notifyTelegramActivity('visit', {
      source: isMarketingSite ? 'marketing' : 'app',
    }, {
      cooldownMinutes: 15,
      cooldownKey: `visit_${hostname}_${window.location.pathname}`,
    })
  }, [hostname, isMarketingSite])

  useEffect(() => {
    let cancelled = false
    const platform = getPlatform()

    if (!isNativeApp() || !['ios', 'android'].includes(platform)) {
      setRequiredUpdate(null)
      setReleaseAnnouncement(null)
      return undefined
    }

    fetch(`${APP_VERSION_CONFIG_URL}?t=${Date.now()}`, { cache: 'no-store' })
      .then(response => response.ok ? response.json() : null)
      .then(config => {
        if (cancelled) return
        const updateRequirement = getRequiredUpdate(config, platform)
        setRequiredUpdate(updateRequirement)
        setReleaseAnnouncement(updateRequirement ? null : getReleaseAnnouncement(config, platform, lang))
      })
      .catch(error => {
        console.warn('Unable to check app version:', error)
      })

    return () => { cancelled = true }
  }, [lang])

  useEffect(() => {
    let active = true
    const params = new URLSearchParams(window.location.search)

    if (!isNativeApp() && params.get('repair') === '1') {
      repairLocalBrowserSession().finally(() => {
        window.history.replaceState({}, '', window.location.pathname)
        window.location.reload()
      })
      return undefined
    }

    const finishLoading = () => {
      if (!active) return
      setLoading(false)
    }

    const restoreTimeout = window.setTimeout(() => {
      console.warn('Auth session restore timed out; continuing to the app.')
      finishLoading()
    }, SESSION_RESTORE_TIMEOUT_MS)

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!active) return
      window.clearTimeout(restoreTimeout)
      if (!session?.user) clearCompanySnapshotStorage()
      if (supportEnabled && isSupportAccount(session?.user)) supportDestinationRef.current = true
      setUser(currentUser => session?.user ?? currentUser)
      if (params.get('payment')) {
        window.history.replaceState({}, '', window.location.pathname)
      }

      finishLoading()
    }).catch(error => {
      console.warn('Unable to restore auth session:', error)
      window.clearTimeout(restoreTimeout)
      finishLoading()
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setScreen('reset-password')
      if (!active) return
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        window.clearTimeout(restoreTimeout)
        finishLoading()
      }
      if (session?.user) {
        if (supportEnabled && isSupportAccount(session.user)) supportDestinationRef.current = true
        setUser(session.user)
        return
      }
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        clearCompanySnapshotStorage()
        if (isNativeApp()) {
          setUser(null)
          clearAppBadge()
          return
        }
        window.setTimeout(async () => {
          if (!active) return
          try {
            const { data: { session: currentSession } } = await supabase.auth.getSession()
            if (currentSession?.user) {
              setUser(currentSession.user)
              return
            }
          } catch (error) {
            console.warn('Unable to recheck auth session after sign-out event:', error)
          }
          if (!active) return
          setUser(null)
          clearAppBadge()
        }, 250)
      }
    })
    return () => {
      active = false
      window.clearTimeout(restoreTimeout)
      subscription.unsubscribe()
    }
  }, [])
  if (loading) return <LoadingIndicator fullScreen background="white" />
if (isMarketingSite) return (
    <>
      <style>{styles + `
        @keyframes pulse { 0%, 100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(0.8); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .waitlist-input:focus { outline: none; border-color: #E24B4A !important; }
        .waitlist-btn:hover { background: #c93a39 !important; }
      `}</style>
      <div className="app">
        <WaitlistScreen />
      </div>
    </>
  )

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <Suspense fallback={<LoadingIndicator fullScreen background="white" />}>
        {screen === 'privacy' ? (
  <PrivacyPolicy setScreen={setScreen} lang={lang} />
) : screen === 'forgot-password' ? (
          <ForgotPasswordScreen setScreen={setScreen} t={t} supportDestination={supportEnabled && supportDestinationRef.current} />
) : supportEnabled && screen === 'support-activate' ? (
          <SupportActivationScreen lang={lang} initialInvitation={typeof getSupportActivationProof==='function'?getSupportActivationProof():undefined} onConsumeInvitation={consumeSupportActivationProof} onBack={()=>setScreen('login')} onLogin={()=>setScreen('support-login')} />
) : supportEnabled && screen === 'support-login' ? (
          <LoginScreen setScreen={setScreen} setUser={setUser} t={t} supportDestination expectedEmail={SUPPORT_ACCOUNT_EMAIL} />
) : screen === 'reset-password' ? (
          <ResetPasswordScreen setScreen={setScreen} t={t} supportDestination={supportEnabled && (supportDestinationRef.current || isSupportAccount(user))} user={user} />
) : screen === 'verification-result' ? (
          <VerificationResultScreen setScreen={setScreen} />
) : screen === 'admin' && !isNativeApp() ? (
  user ? <AdminScreen setScreen={setScreen} user={user} /> : <LoginScreen setScreen={setScreen} setUser={setUser} t={t} />
) : supportEnabled && screen === 'support-app' ? (
  user ? <SupportObserverAccess key={user.id} user={user} onBack={()=>setScreen('support')}><Dashboard key={`support-observer:${user.id}`} user={user} setUser={setUser} t={t} lang={lang} setLang={setLang} isSupportObserver onOpenSupport={()=>setScreen('support')} initialCompanyId={companyLinkRef.current} onCompanyLinkOpened={consumeCompanyLink}/></SupportObserverAccess> : <LoginScreen setScreen={setScreen} setUser={setUser} t={t} supportDestination supportReturnScreen="support-app"/>
) : supportEnabled && (screen === 'support' || (user && isSupportAccount(user))) ? (
  user ? <StaffSupportScreen key={user.id} user={user} lang={lang} onBack={()=>setScreen('app')} onBrowseApp={()=>setScreen('support-app')} onSignOut={async()=>{await signOutCurrentBrowser();clearCompanySnapshotStorage();setUser(null);setScreen('support')}} /> : <LoginScreen setScreen={setScreen} setUser={setUser} t={t} supportDestination />
) : user ? (
  <Dashboard key={user.id} user={user} setUser={setUser} t={t} lang={lang} setLang={setLang} onOpenAdmin={()=>setScreen('admin')} initialTab={requestedTabRef.current} initialCompanyId={companyLinkRef.current} onCompanyLinkOpened={consumeCompanyLink} />
) : screen === 'home' ? (
          <LandingScreen setScreen={setScreen} onRegister={startRegistration} t={t} lang={lang} setLang={setLang} />
        ) : screen === 'login' ? (
          <LoginScreen setScreen={setScreen} setUser={setUser} t={t} supportDestination={supportEnabled && supportDestinationRef.current} />
        ) : screen === 'register' ? (
          <RegisterScreen setScreen={setScreen} onBackHome={returnHomeFromRegistration} t={t} lang={lang} initialSelection={registrationSelection} onSelectionChange={setRegistrationSelection} onPostalRegistrationIntent={pending=>{
            if (pending) {postalSignupReturnTabRef.current=requestedTabRef.current;requestedTabRef.current='profile'}
            else if (postalSignupReturnTabRef.current!==null) {requestedTabRef.current=postalSignupReturnTabRef.current;postalSignupReturnTabRef.current=null}
          }} />
        ) : screen === 'visitor' ? (
          <RegisterScreen setScreen={setScreen} onBackHome={returnHomeFromRegistration} t={t} lang={lang} initialSelection={registrationSelection} onSelectionChange={setRegistrationSelection} />
        ) : screen === 'legal' ? (
          <LegalScreen setScreen={setScreen} lang={lang} />
          ) : screen === 'privacy' ? (
  <PrivacyPolicy setScreen={setScreen} lang={lang} />
        ) : null}
        </Suspense>
      </div>
      {requiredUpdate && <RequiredUpdateScreen updateRequirement={requiredUpdate} />}
      {!requiredUpdate && user && releaseAnnouncement && (
        <ReleaseAnnouncementModal
          announcement={releaseAnnouncement}
          onClose={() => {
            try {
              window.localStorage.setItem(releaseAnnouncement.storageKey, '1')
            } catch {}
            setReleaseAnnouncement(null)
          }}
        />
      )}
    </>
  )
}

function VerificationResultScreen({ setScreen }) {
  const params = new URLSearchParams(window.location.search)
  const title = params.get('title') || 'Validation Hubbing'
  const message = params.get('message') || 'La demande de validation a été traitée.'
  const tone = params.get('tone') === 'error' ? 'error' : 'success'
  const color = tone === 'error' ? '#E24B4A' : '#16a34a'
  const background = tone === 'error' ? '#FFF5F5' : '#F0FDF4'
  const border = tone === 'error' ? '#FECACA' : '#BBF7D0'

  const goHome = () => {
    window.history.replaceState({}, '', window.location.pathname)
    setScreen('home')
  }

  return (
    <div style={{height:'100dvh',display:'flex',alignItems:'center',justifyContent:'center',padding:'calc(env(safe-area-inset-top) + 1.5rem) 1.5rem calc(env(safe-area-inset-bottom) + 1.5rem)',background:'#f7f7f7'}}>
      <section style={{width:'100%',maxWidth:360,background:'white',border:'1px solid #eee',borderRadius:20,padding:'1.5rem',boxShadow:'0 18px 50px rgba(0,0,0,0.08)'}}>
        <img src="/logo192.png" alt="Hubbing" style={{width:64,height:64,borderRadius:16,display:'block',marginBottom:22}} />
        <h1 style={{fontSize:24,lineHeight:1.2,margin:'0 0 14px',fontWeight:800}}>{title}</h1>
        <div style={{background,border:`1px solid ${border}`,borderRadius:14,padding:'1rem',margin:'0 0 1rem'}}>
          <p style={{margin:0,fontSize:14,lineHeight:1.6,color,fontWeight:700}}>{message}</p>
        </div>
        <button onClick={goHome}
          style={{width:'100%',padding:'13px 18px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans'}}>
          Ouvrir Hubbing
        </button>
      </section>
    </div>
  )
}

function RequiredUpdateScreen({ updateRequirement }) {
  const [openingStore, setOpeningStore] = useState(false)

  const openStore = () => {
    setOpeningStore(true)
    window.location.href = updateRequirement.storeUrl
  }

  return (
    <div role="dialog" aria-modal="true" style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(15,23,42,0.72)',display:'flex',alignItems:'center',justifyContent:'center',padding:'calc(env(safe-area-inset-top) + 1.5rem) 1.25rem calc(env(safe-area-inset-bottom) + 1.5rem)',backdropFilter:'blur(8px)'}}>
      <section style={{width:'100%',maxWidth:360,background:'white',borderRadius:24,padding:'1.6rem',boxShadow:'0 28px 70px rgba(0,0,0,0.25)',textAlign:'center',border:'1px solid rgba(255,255,255,0.8)'}}>
        <img src="/LOGO-HUBBING-ICON.svg" alt="Hubbing" style={{width:74,height:74,borderRadius:'50%',boxShadow:'0 16px 40px rgba(226,75,74,0.22)',margin:'0 auto 1rem',display:'block'}} />
        <h1 style={{fontSize:24,lineHeight:1.15,fontWeight:900,color:'#111827',margin:'0 0 0.75rem',letterSpacing:0}}>
          {updateRequirement.title}
        </h1>
        <p style={{fontSize:15,lineHeight:1.55,color:'#64748B',margin:'0 0 1.25rem'}}>
          {updateRequirement.message}
        </p>
        <button type="button" onClick={openStore}
          style={{width:'100%',padding:'14px 18px',background:'#E24B4A',color:'white',border:'none',borderRadius:14,fontSize:16,fontWeight:900,cursor:'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:'0 14px 34px rgba(226,75,74,0.28)'}}>
          {openingStore ? 'Ouverture...' : updateRequirement.buttonLabel}
        </button>
        <p style={{fontSize:11,lineHeight:1.45,color:'#94A3B8',margin:'0.9rem 0 0'}}>
          Version installée : {APP_VERSION} ({APP_BUILD_NUMBER})
        </p>
      </section>
    </div>
  )
}

function ReleaseAnnouncementModal({ announcement, onClose }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={announcement.title}
      style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(15,23,42,0.68)',display:'flex',alignItems:'center',justifyContent:'center',padding:'calc(env(safe-area-inset-top) + 1.5rem) 1.25rem calc(env(safe-area-inset-bottom) + 1.5rem)',backdropFilter:'blur(8px)'}}>
      <section style={{width:'100%',maxWidth:370,maxHeight:'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 3rem)',overflowY:'auto',WebkitOverflowScrolling:'touch',background:'white',borderRadius:24,padding:'1.5rem',boxSizing:'border-box',boxShadow:'0 28px 70px rgba(0,0,0,0.25)',border:'1px solid rgba(255,255,255,0.8)'}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:14,marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',gap:12,minWidth:0}}>
            <img src="/LOGO-HUBBING-ICON.svg" alt="" style={{width:56,height:56,borderRadius:'50%',boxShadow:'0 10px 26px rgba(226,75,74,0.2)',flexShrink:0}} />
            <div style={{minWidth:0}}>
              <p style={{fontSize:10,color:'#E24B4A',fontWeight:900,letterSpacing:0.4,margin:'0 0 4px',textTransform:'uppercase'}}>Version {announcement.version}</p>
              <h1 style={{fontSize:22,lineHeight:1.2,fontWeight:900,color:'#111827',margin:0}}>{announcement.title}</h1>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer"
            style={{width:38,height:38,borderRadius:'50%',border:'1px solid #E2E8F0',background:'#F8FAFC',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0,cursor:'pointer'}}>
            <HubbingIcon name="x" size={18} color="#475569" />
          </button>
        </div>
        <p style={{fontSize:14,lineHeight:1.6,color:'#64748B',margin:'0 0 14px'}}>{announcement.message}</p>
        {announcement.bullets.length > 0 && (
          <div style={{display:'flex',flexDirection:'column',gap:9,marginBottom:18}}>
            {announcement.bullets.map((item, index) => (
              <div key={`${index}-${item}`} style={{display:'grid',gridTemplateColumns:'26px minmax(0, 1fr)',alignItems:'start',gap:9,background:'#FFF8F7',border:'1px solid #FEE2E2',borderRadius:12,padding:'10px 11px'}}>
                <span aria-hidden="true" style={{width:25,height:25,borderRadius:'50%',background:'#E24B4A',color:'white',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:900}}>✓</span>
                <p style={{fontSize:12,lineHeight:1.5,color:'#334155',fontWeight:700,margin:'2px 0 0'}}>{item}</p>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={onClose}
          style={{width:'100%',padding:'13px 16px',background:'#E24B4A',color:'white',border:'none',borderRadius:13,fontSize:15,fontWeight:900,cursor:'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:'0 12px 28px rgba(226,75,74,0.24)'}}>
          {announcement.buttonLabel}
        </button>
      </section>
    </div>
  )
}

function PlanBadge({ plan = 'Starter' }) {
  return <VerifiedBadge size={29} variant={plan.toLowerCase()} />
}

function InstallAppButton({ compact = false }) {
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null)
  const [showInstallHelp, setShowInstallHelp] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    const updateStandalone = () => {
      setIsStandalone(window.matchMedia?.('(display-mode: standalone)').matches || window.navigator?.standalone === true)
    }
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault()
      setDeferredInstallPrompt(event)
    }
    const handleInstalled = () => {
      setIsStandalone(true)
      setDeferredInstallPrompt(null)
      setShowInstallHelp(false)
    }

    updateStandalone()
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleInstalled)
    }
  }, [])

  const handleInstallApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt()
      const choice = await deferredInstallPrompt.userChoice
      setDeferredInstallPrompt(null)
      if (choice?.outcome !== 'accepted') setShowInstallHelp(true)
      return
    }
    setShowInstallHelp(value => !value)
  }

  if (isNativeApp()) return null

  return (
    <div style={{width:'100%',maxWidth:compact ? '100%' : 340}}>
      <button
        onClick={handleInstallApp}
        disabled={isStandalone}
        style={{width:'100%',padding:compact ? '12px 14px' : '13px 16px',background:isStandalone ? '#f0fdf4' : '#1a1a1a',color:isStandalone ? '#15803d' : 'white',border:isStandalone ? '1px solid #bbf7d0' : 'none',borderRadius:12,fontSize:compact ? 13 : 14,fontWeight:700,cursor:isStandalone ? 'default' : 'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:isStandalone ? 'none' : '0 8px 24px rgba(0,0,0,0.12)'}}>
        {isStandalone ? 'Application installée' : "Installer l'application sur ce téléphone"}
      </button>
      {showInstallHelp && !isStandalone && (
        <div style={{marginTop:10,background:'#f9f9f9',border:'1px solid #eee',borderRadius:12,padding:'0.85rem',textAlign:'left'}}>
          <p style={{fontSize:12,color:'#1a1a1a',fontWeight:700,margin:'0 0 6px'}}>Installation sur téléphone</p>
          <p style={{fontSize:12,color:'#666',lineHeight:1.5,margin:0}}>
            Sur iPhone : ouvrez cette page dans Safari, touchez le bouton Partager, puis "Ajouter à l'écran d'accueil".
          </p>
        </div>
      )}
    </div>
  )
}

const MARKETING_LANGUAGES = [
  { code: 'fr', word: 'Bienvenue' },
  { code: 'de', word: 'Willkommen' },
  { code: 'it', word: 'Benvenuti' },
  { code: 'en', word: 'Welcome' },
]

const MARKETING_COPY = {
  fr: {
    status: 'Application ouverte aux entreprises suisses',
    appStore: "Télécharger sur l'App Store",
    googlePlay: 'Télécharger sur Google Play',
    openApp: "Ouvrir l'application",
    headlineLines: ['Le réseau B2B suisse', 'pour trouver les bons'],
    headlineHighlight: 'partenaires locaux',
    description: 'Hubbing met en relation les entreprises suisses pour trouver des partenaires, répondre à des besoins concrets et développer des collaborations locales.',
    needEyebrow: 'Appels d’offres ciblés',
    needTitle: "Recevez les opportunités qui correspondent à vos services",
    needDesc: "Publiez vos besoins ou répondez aux appels d’offres des entreprises qui recherchent exactement ce que vous proposez. Les notifications vous préviennent quand une opportunité pertinente apparaît.",
    needPills: ['Besoins ciblés', 'Notifications', 'Services proposés'],
    matchText: 'TechSoft Zürich AG vous a matché !',
    contactLabel: 'Contacter',
    mapCount: '9 entreprises',
    mapNearby: 'près de vous',
    plansEyebrow: 'Abonnements',
    plansTitle: 'Choisissez le plan adapté',
    planCta: 'Voir le plan',
    planFeatures: {
      starter: ['Profil entreprise', '5 swipes par jour', '5 messages/jour'],
      basic: ['Swipes illimités', 'Messages illimités', 'Adresse visible'],
      premium: ['Tout Basic inclus', 'Coordonnées complètes', 'Pièces jointes'],
    },
    features: [
      { icon: 'briefcase', title: 'Swipe B2B', desc: 'Découvrez des entreprises locales et matchez avec celles qui correspondent à vos besoins.' },
      { icon: 'map', title: 'Carte interactive', desc: 'Visualisez les entreprises autour de vous, filtrez par secteur, canton ou rayon.' },
      { icon: 'message', title: 'Messagerie pro', desc: 'Échangez avec vos connexions B2B et partagez des documents en toute sécurité.' },
    ],
    questions: 'Des questions ?',
    made: 'Made in Switzerland',
    rights: 'Tous droits réservés',
  },
  de: {
    status: 'App offen für Schweizer Unternehmen',
    appStore: 'Im App Store laden',
    googlePlay: 'Bei Google Play laden',
    openApp: 'App öffnen',
    headlineLines: ['Das Schweizer B2B-Netzwerk', 'für die richtigen'],
    headlineHighlight: 'lokalen Partner',
    description: 'Hubbing verbindet Schweizer Unternehmen, damit sie Partner finden, auf konkrete Bedürfnisse reagieren und lokale Kooperationen entwickeln können.',
    needEyebrow: 'Gezielte Ausschreibungen',
    needTitle: 'Erhalten Sie Chancen, die zu Ihren Leistungen passen',
    needDesc: 'Veröffentlichen Sie Ihren Bedarf oder antworten Sie auf Ausschreibungen von Unternehmen, die genau Ihre Leistungen suchen. Benachrichtigungen informieren Sie bei passenden Chancen.',
    needPills: ['Gezielter Bedarf', 'Benachrichtigungen', 'Ihre Leistungen'],
    matchText: 'TechSoft Zürich AG hat mit Ihnen gematcht!',
    contactLabel: 'Kontaktieren',
    mapCount: '9 Unternehmen',
    mapNearby: 'in Ihrer Nähe',
    plansEyebrow: 'Abonnements',
    plansTitle: 'Wählen Sie den passenden Plan',
    planCta: 'Plan ansehen',
    planFeatures: {
      starter: ['Unternehmensprofil', '5 Swipes pro Tag', '5 Nachrichten/Tag'],
      basic: ['Unbegrenzte Swipes', 'Unbegrenzte Nachrichten', 'Adresse sichtbar'],
      premium: ['Alles aus Basic', 'Vollständige Kontaktdaten', 'Anhänge'],
    },
    features: [
      { icon: 'briefcase', title: 'B2B-Swipe', desc: 'Entdecken Sie lokale Unternehmen und matchen Sie passende Partner.' },
      { icon: 'map', title: 'Interaktive Karte', desc: 'Finden Sie Unternehmen in Ihrer Nähe und filtern Sie nach Branche, Kanton oder Radius.' },
      { icon: 'message', title: 'Business-Nachrichten', desc: 'Tauschen Sie sich mit B2B-Kontakten aus und teilen Sie Dokumente sicher.' },
    ],
    questions: 'Fragen?',
    made: 'Made in Switzerland',
    rights: 'Alle Rechte vorbehalten',
  },
  it: {
    status: 'Applicazione aperta alle aziende svizzere',
    appStore: "Scarica dall'App Store",
    googlePlay: 'Scarica da Google Play',
    openApp: "Apri l'app",
    headlineLines: ['La rete B2B svizzera', 'per trovare i giusti'],
    headlineHighlight: 'partner locali',
    description: 'Hubbing mette in contatto aziende svizzere per trovare partner, rispondere a bisogni concreti e sviluppare collaborazioni locali.',
    needEyebrow: 'Richieste mirate',
    needTitle: 'Ricevi opportunità in linea con i tuoi servizi',
    needDesc: 'Pubblica i tuoi bisogni o rispondi alle richieste delle aziende che cercano esattamente ciò che offri. Le notifiche ti avvisano quando appare un’opportunità pertinente.',
    needPills: ['Bisogni mirati', 'Notifiche', 'Servizi offerti'],
    matchText: 'TechSoft Zürich AG ha fatto match con te!',
    contactLabel: 'Contattare',
    mapCount: '9 aziende',
    mapNearby: 'vicino a te',
    plansEyebrow: 'Abbonamenti',
    plansTitle: 'Scegli il piano adatto',
    planCta: 'Vedi piano',
    planFeatures: {
      starter: ['Profilo aziendale', '5 swipe al giorno', '5 messaggi/giorno'],
      basic: ['Swipe illimitati', 'Messaggi illimitati', 'Indirizzo visibile'],
      premium: ['Tutto Basic incluso', 'Contatti completi', 'Allegati'],
    },
    features: [
      { icon: 'briefcase', title: 'Swipe B2B', desc: 'Scopri aziende locali e crea match con partner pertinenti.' },
      { icon: 'map', title: 'Mappa interattiva', desc: 'Visualizza le aziende intorno a te e filtra per settore, cantone o raggio.' },
      { icon: 'message', title: 'Messaggistica pro', desc: 'Scambia messaggi con i tuoi contatti B2B e condividi documenti in sicurezza.' },
    ],
    questions: 'Domande?',
    made: 'Made in Switzerland',
    rights: 'Tutti i diritti riservati',
  },
  en: {
    status: 'Open to Swiss companies',
    appStore: 'Download on the App Store',
    googlePlay: 'Get it on Google Play',
    openApp: 'Open the app',
    headlineLines: ['The Swiss B2B network', 'to find the right'],
    headlineHighlight: 'local partners',
    description: 'Hubbing connects Swiss companies so they can find partners, respond to concrete needs and build local business collaborations.',
    needEyebrow: 'Targeted tenders',
    needTitle: 'Receive opportunities that match your services',
    needDesc: 'Publish your needs or respond to tenders from companies looking for exactly what you offer. Notifications alert you when a relevant opportunity appears.',
    needPills: ['Targeted needs', 'Notifications', 'Offered services'],
    matchText: 'TechSoft Zürich AG matched with you!',
    contactLabel: 'Contact',
    mapCount: '9 companies',
    mapNearby: 'near you',
    plansEyebrow: 'Subscriptions',
    plansTitle: 'Choose the right plan',
    planCta: 'View plan',
    planFeatures: {
      starter: ['Company profile', '5 swipes per day', '5 messages/day'],
      basic: ['Unlimited swipes', 'Unlimited messages', 'Visible address'],
      premium: ['Everything in Basic', 'Full contact details', 'Attachments'],
    },
    features: [
      { icon: 'briefcase', title: 'B2B Swipe', desc: 'Discover local companies and match with relevant business partners.' },
      { icon: 'map', title: 'Interactive map', desc: 'See companies around you and filter by sector, canton or distance.' },
      { icon: 'message', title: 'Business messaging', desc: 'Chat with your B2B connections and share documents securely.' },
    ],
    questions: 'Questions?',
    made: 'Made in Switzerland',
    rights: 'All rights reserved',
  },
}

function WaitlistScreen() {
  const [marketingLang, setMarketingLang] = useState('fr')
  const copy = MARKETING_COPY[marketingLang] || MARKETING_COPY.fr

  const Feature = ({ icon, title, desc }) => (
    <div style={{background:'#f9f9f9',borderRadius:16,padding:'1.25rem',textAlign:'left',width:'100%',maxWidth:340}}>
      <div style={{marginBottom:8}}>
        <HubbingIcon name={icon} size={28} />
      </div>
      <p style={{fontSize:14,fontWeight:700,color:'#1a1a1a',margin:'0 0 4px'}}>{title}</p>
      <p style={{fontSize:13,color:'#666',margin:0,lineHeight:1.5}}>{desc}</p>
    </div>
  )

  const PlanPhone = ({ name, price, variant, color, features, recommended, cta }) => (
    <div style={{background:'white',borderRadius:20,padding:'0.75rem',boxShadow:'0 8px 30px rgba(0,0,0,0.12)',width:104,minHeight:172,flexShrink:0,border:`1px solid ${color}33`,display:'flex',flexDirection:'column',gap:7,textAlign:'left'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:4}}>
        <span style={{fontSize:11,fontWeight:800,color,display:'inline-flex',alignItems:'center',gap:3}}>
          {name}
          <VerifiedBadge size={13} variant={variant} />
        </span>
        {recommended && (
          <span style={{fontSize:6,fontWeight:800,color:'white',background:'#E24B4A',borderRadius:999,padding:'3px 5px'}}>
            TOP
          </span>
        )}
      </div>
      <p style={{fontSize:13,fontWeight:900,color:'#111827',margin:0,lineHeight:1.1}}>{price}</p>
      <div style={{display:'flex',flexDirection:'column',gap:5,flex:1}}>
        {features.map((feature) => (
          <p key={feature} style={{fontSize:8.5,color:'#64748B',lineHeight:1.25,margin:0,display:'flex',gap:3}}>
            <span style={{color,fontWeight:900}}>✓</span>
            <span>{feature}</span>
          </p>
        ))}
      </div>
      <div style={{background:color,borderRadius:8,padding:'6px 5px',textAlign:'center',marginTop:'auto'}}>
        <span style={{fontSize:8,fontWeight:800,color:'white'}}>{cta}</span>
      </div>
    </div>
  )

  return (
    <div style={{height:'100dvh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-start',padding:'calc(env(safe-area-inset-top) + 1rem) 2rem calc(env(safe-area-inset-bottom) + 10rem)',gap:'1.5rem',textAlign:'center',position:'relative',background:'white',overflowY:'auto',overflowX:'hidden',WebkitOverflowScrolling:'touch'}}>

      {/* Logo */}
      <div style={{animation:'fadeUp 0.6s ease 0.1s both',display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
        <img src="/LOGO-HUBBING-ICON.svg" alt="Hubbing" style={{width:112,height:112,borderRadius:'50%',boxShadow:'0 18px 45px rgba(226,75,74,0.22)'}} />
        <h1 style={{fontSize:38,fontWeight:800,fontFamily:'Nunito, sans-serif',letterSpacing:'-0.5px',margin:0}}>hubbing</h1>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,width:'100%',maxWidth:340,animation:'fadeUp 0.6s ease 0.25s both'}}>
        {MARKETING_LANGUAGES.map((item) => (
          <button
            key={item.code}
            type="button"
            onClick={() => setMarketingLang(item.code)}
            aria-pressed={marketingLang === item.code}
            style={{background:marketingLang === item.code ? '#E24B4A' : '#f9f9f9',color:marketingLang === item.code ? 'white' : '#1a1a1a',border:marketingLang === item.code ? 'none' : '1px solid #eee',borderRadius:14,padding:'0.8rem 0.65rem',fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans'}}>
            {item.word}
          </button>
        ))}
      </div>

      <div style={{display:'inline-flex',alignItems:'center',gap:8,background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:100,padding:'8px 18px',animation:'fadeUp 0.6s ease 0.3s both'}}>
        <div style={{width:7,height:7,borderRadius:'50%',background:'#16a34a',animation:'pulse 2s ease infinite'}}></div>
        <span style={{fontSize:13,fontWeight:700,color:'#15803d'}}>{copy.status}</span>
      </div>

      <div style={{width:'100%',maxWidth:340,animation:'fadeUp 0.6s ease 0.35s both'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{minHeight:50,padding:'12px 10px',background:'#111827',color:'white',border:'none',borderRadius:12,fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:'0 8px 24px rgba(17,24,39,0.14)',display:'flex',alignItems:'center',justifyContent:'center',gap:7,textDecoration:'none',lineHeight:1.2}}>
            <HubbingIcon name="apple" size={18} color="white" />
            <span>{copy.appStore}</span>
          </a>
          <a
            href={ANDROID_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{minHeight:50,padding:'12px 10px',background:'#16A34A',color:'white',border:'none',borderRadius:12,fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:'0 8px 24px rgba(22,163,74,0.16)',display:'flex',alignItems:'center',justifyContent:'center',gap:7,textDecoration:'none',lineHeight:1.2}}>
            <HubbingIcon name="smartphone" size={18} color="white" />
            <span>{copy.googlePlay}</span>
          </a>
        </div>
        <a
          href="https://app.hubbing.ch"
          style={{width:'100%',marginTop:10,padding:'13px 16px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:'0 8px 24px rgba(226,75,74,0.18)',display:'flex',alignItems:'center',justifyContent:'center',gap:8,textDecoration:'none'}}>
          {copy.openApp}
        </a>
      </div>

      {/* Headline forte */}
      <div style={{animation:'fadeUp 0.6s ease 0.4s both'}}>
        <h2 style={{fontSize:26,fontWeight:800,letterSpacing:'-0.5px',lineHeight:1.2,marginBottom:'0.75rem'}}>
          {copy.headlineLines[0]}<br />
          {copy.headlineLines[1]}<br />
          <span style={{color:'#E24B4A'}}>{copy.headlineHighlight}</span>
        </h2>
        <p style={{color:'#666',fontSize:14,lineHeight:1.7,maxWidth:300,margin:'0 auto'}}>
          {copy.description}
        </p>
      </div>

      {/* Mockup screenshots */}
      <div style={{display:'flex',gap:10,justifyContent:'center',alignItems:'stretch',animation:'fadeUp 0.6s ease 0.5s both',overflow:'visible',width:'100%',maxWidth:340,minHeight:168,flexShrink:0}}>
        {/* Screenshot 1 - Swipe */}
        <div style={{background:'white',borderRadius:20,padding:'0.75rem',boxShadow:'0 8px 30px rgba(0,0,0,0.12)',width:96,minHeight:160,flexShrink:0,border:'1px solid #f0f0f0',display:'flex',flexDirection:'column'}}>
          <div style={{background:'#E24B4A',borderRadius:12,padding:'0.75rem',marginBottom:8,textAlign:'center'}}>
            <HubbingIcon name="building" size={28} color="white" />
          </div>
          <p style={{fontSize:9,fontWeight:700,margin:'0 0 2px',color:'#1a1a1a'}}>Fiduciaire Rochat</p>
          <p style={{fontSize:8,color:'#999',margin:'0 0 6px'}}>Lausanne, VD</p>
          <div style={{display:'flex',justifyContent:'center',gap:6}}>
            <div style={{width:24,height:24,borderRadius:'50%',border:'2px solid #E24B4A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10}}>✗</div>
            <div style={{width:28,height:28,borderRadius:'50%',background:'#E24B4A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'white'}}>✓</div>
          </div>
          <p style={{fontSize:8,color:'#E24B4A',fontWeight:600,marginTop:6,textAlign:'center'}}>SWIPE</p>
        </div>

        {/* Screenshot 2 - Match */}
        <div style={{background:'white',borderRadius:20,padding:'0.75rem',boxShadow:'0 8px 30px rgba(0,0,0,0.12)',width:96,minHeight:160,flexShrink:0,border:'1px solid #f0f0f0',display:'flex',flexDirection:'column'}}>
          <div style={{background:'#22c55e',borderRadius:12,padding:'0.5rem',marginBottom:6,textAlign:'center'}}>
            <p style={{color:'white',fontWeight:800,fontSize:11,margin:0}}>MATCH !</p>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:6}}>
            <div style={{width:20,height:20,borderRadius:'50%',background:'#185FA5',display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,color:'white',fontWeight:700}}>TE</div>
            <div style={{width:20,height:20,borderRadius:'50%',background:'#E24B4A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,color:'white',fontWeight:700}}>HB</div>
          </div>
          <p style={{fontSize:8,color:'#666',margin:'0 0 6px',lineHeight:1.4}}>{copy.matchText}</p>
          <div style={{background:'#E24B4A',borderRadius:8,padding:'4px',textAlign:'center'}}>
            <p style={{color:'white',fontSize:8,fontWeight:600,margin:0}}>{copy.contactLabel}</p>
          </div>
          <p style={{fontSize:8,color:'#185FA5',fontWeight:600,marginTop:6,textAlign:'center'}}>MESSAGES</p>
        </div>

        {/* Screenshot 3 - Carte */}
        <div style={{background:'white',borderRadius:20,padding:'0.75rem',boxShadow:'0 8px 30px rgba(0,0,0,0.12)',width:96,minHeight:160,flexShrink:0,border:'1px solid #f0f0f0',display:'flex',flexDirection:'column'}}>
          <div style={{background:'#f0f9ff',borderRadius:12,padding:'0.5rem',marginBottom:6,position:'relative',overflow:'hidden',height:50}}>
            {/* Mini carte simulée */}
            <div style={{position:'absolute',top:8,left:12,width:8,height:8,borderRadius:'50%',background:'#E24B4A'}}></div>
            <div style={{position:'absolute',top:20,left:30,width:6,height:6,borderRadius:'50%',background:'#185FA5'}}></div>
            <div style={{position:'absolute',top:12,left:50,width:7,height:7,borderRadius:'50%',background:'#22c55e'}}></div>
            <div style={{position:'absolute',top:25,left:20,width:5,height:5,borderRadius:'50%',background:'#F39C12'}}></div>
            <div style={{position:'absolute',top:8,left:60,width:6,height:6,borderRadius:'50%',background:'#E24B4A'}}></div>
          </div>
          <p style={{fontSize:9,fontWeight:700,margin:'0 0 2px',color:'#1a1a1a'}}>{copy.mapCount}</p>
          <p style={{fontSize:8,color:'#999',margin:'0 0 4px'}}>{copy.mapNearby}</p>
          <div style={{background:'#f5f5f5',borderRadius:6,padding:'3px',textAlign:'center'}}>
            <p style={{fontSize:7,color:'#666',margin:0}}>VD · Informatique</p>
          </div>
          <p style={{fontSize:8,color:'#22c55e',fontWeight:600,marginTop:6,textAlign:'center'}}>CARTE</p>
        </div>
      </div>

      <div style={{width:'100%',maxWidth:340,animation:'fadeUp 0.6s ease 0.56s both',background:'linear-gradient(135deg, #FFF7ED 0%, #FFF5F5 100%)',border:'1px solid #FED7AA',borderRadius:18,padding:'1rem',textAlign:'left',boxShadow:'0 12px 34px rgba(249,115,22,0.10)'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
          <div style={{width:38,height:38,borderRadius:12,background:'#E24B4A',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <HubbingIcon name="briefcase" size={21} color="white" />
          </div>
          <div>
            <p style={{fontSize:11,color:'#EA580C',fontWeight:900,textTransform:'uppercase',letterSpacing:0.4,margin:'0 0 2px'}}>{copy.needEyebrow}</p>
            <h3 style={{fontSize:18,fontWeight:900,color:'#111827',lineHeight:1.22,margin:0}}>{copy.needTitle}</h3>
          </div>
        </div>
        <p style={{fontSize:13,color:'#64748B',lineHeight:1.55,margin:'0 0 12px'}}>{copy.needDesc}</p>
        <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
          {copy.needPills.map((pill) => (
            <span key={pill} style={{fontSize:11,fontWeight:800,color:'#9A3412',background:'rgba(255,255,255,0.78)',border:'1px solid #FDBA74',borderRadius:999,padding:'6px 9px'}}>
              {pill}
            </span>
          ))}
        </div>
      </div>

      {/* Plans */}
      <div style={{width:'100%',maxWidth:340,animation:'fadeUp 0.6s ease 0.6s both'}}>
        <div style={{textAlign:'center',marginBottom:12}}>
          <p style={{fontSize:12,color:'#E24B4A',fontWeight:800,margin:'0 0 4px',textTransform:'uppercase',letterSpacing:0.4}}>{copy.plansEyebrow}</p>
          <h3 style={{fontSize:22,fontWeight:900,color:'#111827',letterSpacing:'-0.3px',lineHeight:1.2,margin:0}}>{copy.plansTitle}</h3>
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'center',alignItems:'stretch',overflow:'visible',width:'100%',minHeight:178}}>
          <PlanPhone
            name="Starter"
            price="Gratuit"
            variant="starter"
            color="#A16207"
            features={copy.planFeatures.starter}
            cta={copy.planCta}
          />
          <PlanPhone
            name="Basic"
            price="CHF 19.-"
            variant="basic"
            color="#185FA5"
            features={copy.planFeatures.basic}
            cta={copy.planCta}
          />
          <PlanPhone
            name="Premium"
            price="CHF 39.-"
            variant="premium"
            color="#E24B4A"
            recommended
            features={copy.planFeatures.premium}
            cta={copy.planCta}
          />
        </div>
      </div>

      {/* Features */}
      <div style={{display:'flex',flexDirection:'column',gap:12,width:'100%',maxWidth:340,animation:'fadeUp 0.6s ease 0.75s both'}}>
        {copy.features.map((feature) => (
          <Feature key={feature.title} icon={feature.icon} title={feature.title} desc={feature.desc} />
        ))}
      </div>

      <p style={{fontSize:13,color:'#bbb',animation:'fadeUp 0.6s ease 1s both'}}>
        {copy.questions} <a href="mailto:contact@hubbing.ch" style={{color:'#E24B4A',textDecoration:'none',fontWeight:500}}>contact@hubbing.ch</a>
      </p>
      <p style={{fontSize:12,color:'#ccc',textAlign:'center'}}>🇨🇭 {copy.made}</p>
      <p style={{fontSize:11,color:'#ddd',textAlign:'center'}}>© {new Date().getFullYear()} Hubbing — {copy.rights}</p>

      <div style={{position:'fixed',left:'50%',bottom:0,transform:'translateX(-50%)',width:'100%',maxWidth:430,background:'white',borderTop:'1px solid #f0f0f0',boxShadow:'0 -8px 30px rgba(0,0,0,0.08)',padding:'0.85rem 1rem calc(env(safe-area-inset-bottom) + 0.85rem)',zIndex:20}}>
        <div style={{display:'flex',flexDirection:'column',gap:'0.6rem'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{padding:'11px 10px',background:'#111827',color:'white',borderRadius:12,fontSize:12,fontWeight:800,textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'center',gap:6,lineHeight:1.15}}>
              <HubbingIcon name="apple" size={16} color="white" />
              <span>{copy.appStore}</span>
            </a>
            <a
              href={ANDROID_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{padding:'11px 10px',background:'#16A34A',color:'white',borderRadius:12,fontSize:12,fontWeight:800,textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'center',gap:6,lineHeight:1.15}}>
              <HubbingIcon name="smartphone" size={16} color="white" />
              <span>{copy.googlePlay}</span>
            </a>
          </div>
          <a
            href="https://app.hubbing.ch"
            style={{width:'100%',padding:'13px 14px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans',boxShadow:'0 8px 24px rgba(226,75,74,0.18)',textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'center'}}>
            {copy.openApp}
          </a>
        </div>
      </div>
    </div>
  )
}
function LandingScreen({ setScreen, onRegister, t, lang, setLang }) {
  const [showLangMenu, setShowLangMenu] = useState(false)
  const [showUsageGuide, setShowUsageGuide] = useState(false)
  const [showIntro, setShowIntro] = useState(false)
  const introLabel = { fr: 'Découvrir Hubbing', de: 'Hubbing entdecken', it: 'Scopri Hubbing', en: 'Discover Hubbing' }[lang] || 'Découvrir Hubbing'
  const usageGuideText = getUiText(lang).usageGuide
  const langs = [
    { code: 'fr', label: '🇫🇷 Français' },
    { code: 'de', label: '🇩🇪 Deutsch' },
    { code: 'it', label: '🇮🇹 Italiano' },
    { code: 'en', label: '🇬🇧 English' },
  ]
  const openVisitorPricing = () => onRegister()

  return (
    <div className="hb-landing" style={{height:'100dvh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-start',padding:'calc(env(safe-area-inset-top) + 4.5rem) 1rem calc(env(safe-area-inset-bottom) + 1.5rem)',gap:'1rem',position:'relative',background:'white',overflowY:'auto',overflowX:'hidden',WebkitOverflowScrolling:'touch'}}>
      <LandingNetwork />
      {showUsageGuide && <UsageGuideModal t={usageGuideText} onClose={() => setShowUsageGuide(false)} />}
      {showIntro && <Suspense fallback={<div role="status" aria-label={introLabel} style={{position:'absolute',top:'calc(env(safe-area-inset-top) + 4.1rem)',height:2,width:72,borderRadius:2,background:'#E24B4A'}} />}><HubbingIntro lang={lang} onClose={() => setShowIntro(false)} onRegister={() => { setShowIntro(false); onRegister('starter') }} /></Suspense>}
      <div style={{position:'absolute',top:'calc(env(safe-area-inset-top) + 1rem)',right:'1rem'}}>
        <button onClick={() => setShowLangMenu(!showLangMenu)}
          style={{background:'#f5f5f5',border:'1px solid #eee',borderRadius:20,padding:'6px 14px',fontSize:13,cursor:'pointer',fontFamily:'Plus Jakarta Sans',fontWeight:500,display:'inline-flex',alignItems:'center',gap:6}}>
          <HubbingIcon name="globe" size={15} color="#3B82F6" />
          {t.changeLanguage}
        </button>
        {showLangMenu && (
          <div style={{position:'absolute',right:0,top:'110%',background:'white',border:'1px solid #eee',borderRadius:12,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',overflow:'hidden',zIndex:100}}>
            {langs.map(l => (
              <button key={l.code} onClick={() => { setLang(l.code); setShowLangMenu(false) }}
                style={{display:'block',width:'100%',padding:'10px 20px',background: lang === l.code ? '#FFF5F5' : 'white',border:'none',cursor:'pointer',fontSize:14,textAlign:'left',fontFamily:'Plus Jakarta Sans',color: lang === l.code ? '#E24B4A' : '#333',fontWeight: lang === l.code ? 600 : 400}}>
                {l.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button type="button" onClick={() => { setShowUsageGuide(false); setShowIntro(true) }} aria-haspopup="dialog"
        style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:8,minHeight:44,padding:'8px 16px',border:'1px solid #EADDD6',borderRadius:24,background:'#FFF9F6',color:'#7F3937',fontFamily:'Plus Jakarta Sans',fontSize:13,fontWeight:700,cursor:'pointer',boxShadow:'0 3px 12px rgba(36,58,69,0.04)'}}>
        <HubbingIcon name="compass" size={16} color="#C43C3B" />
        {introLabel}
      </button>
      <img className="hb-landing-logo" src="/LOGO-HUBBING.svg" alt="Hubbing" style={{width:116,height:116,borderRadius:26,boxShadow:'0 14px 32px rgba(226,75,74,0.18)'}} />
      <div style={{textAlign:'center'}}>
        <p style={{color:'#475569',fontSize:16,lineHeight:1.55,margin:'0 0 10px',fontWeight:600}}>{t.appTagline}</p>
        <p style={{color:'#64748B',fontSize:13,lineHeight:1.6,margin:0}}>{t.needsNotificationTagline}</p>
      </div>
      <button onClick={openVisitorPricing}
        style={{width:'100%',padding:'14px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:16,fontWeight:600,cursor:'pointer'}}>
        {t.createAccount}
      </button>
      <button onClick={() => setScreen('login')}
        style={{width:'100%',padding:'14px',background:'white',color:'#E24B4A',border:'2px solid #E24B4A',borderRadius:12,fontSize:16,fontWeight:600,cursor:'pointer'}}>
        {t.login}
      </button>
      <button onClick={() => { setShowIntro(false); setShowUsageGuide(true) }}
        style={{width:'100%',padding:'13px 14px',background:'white',color:'#185FA5',border:'1px solid #BFDBFE',borderRadius:12,fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:'Plus Jakarta Sans',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
        <HubbingIcon name="sparkles" size={17} color="#185FA5" />
        {t.usageGuide}
      </button>
      <div style={{width:'100%',display:'grid',gridTemplateColumns:'repeat(3, minmax(0, 1fr))',gap:8}}>
        {t.planPreviews.map((plan, index) => {
          const colors = ['#64748B', '#185FA5', '#E24B4A']
          const color = colors[index] || '#E24B4A'
          return (
            <button key={plan.name} onClick={() => onRegister(plan.id)}
              style={{minWidth:0,minHeight:104,padding:'0.75rem 0.55rem',background:'white',border:`1px solid ${color}33`,borderRadius:12,boxShadow:'0 8px 20px rgba(15,23,42,0.06)',cursor:'pointer',fontFamily:'Plus Jakarta Sans',display:'flex',flexDirection:'column',alignItems:'flex-start',justifyContent:'space-between',textAlign:'left'}}>
              <span style={{fontSize:12,fontWeight:800,color,whiteSpace:'nowrap',display:'inline-flex',alignItems:'center',gap:4}}>
                {plan.name}
                <VerifiedBadge size={14} variant={plan.id} />
              </span>
              <span style={{fontSize:12,fontWeight:800,color:'#111827',lineHeight:1.25,display:'flex',flexWrap:'wrap',gap:'0 3px'}}>{plan.price.split('/').map((part, i) => <span key={i} style={{whiteSpace:'nowrap'}}>{i ? '/' : ''}{part}</span>)}</span>
              <span style={{fontSize:10,color:'#64748B',lineHeight:1.25,overflowWrap:'normal'}}>{plan.detail}</span>
            </button>
          )
        })}
      </div>
      <button onClick={openVisitorPricing}
        style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:'#E24B4A',fontWeight:700,fontFamily:'Plus Jakarta Sans'}}>
        {t.planPreviewCta} →
      </button>
      <button onClick={() => setScreen('legal')}
        style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:'#bbb',textDecoration:'underline'}}>
        {t.legal}
      </button>
      <p style={{fontSize:11,color:'#ccc',textAlign:'center',margin:0}}>© {new Date().getFullYear()} Hubbing — Tous droits réservés</p>
    </div>
  )
}
export function LoginScreen({ setScreen, setUser, t, supportDestination = false, expectedEmail = null, supportReturnScreen = 'support' }) {
  const supportIntent = staffSupportEnabled(window.location.hostname, isNativeApp()) && (supportDestination || hasSupportDestination(window.location.search))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [repairing, setRepairing] = useState(false)
  const showRepairLogin = !isNativeApp()
  const [attemptPending, setAttemptPending] = useState(false)
  const pendingAttemptRef = useRef(null)
  const loginGenerationRef = useRef(0)
  const loginMountedRef = useRef(false)
  useEffect(() => {loginMountedRef.current=true;return () => {loginMountedRef.current=false;loginGenerationRef.current+=1}},[])

  const handleLogin = async () => {
    if (pendingAttemptRef.current) return
    const generation = ++loginGenerationRef.current
    const current = () => loginMountedRef.current && generation === loginGenerationRef.current
    setLoading(true)
    setAttemptPending(true)
    setError('')
    const normalizedEmail = (expectedEmail || email).trim().toLowerCase()
    const attempt = startLoginAttempt(supabase,{email:normalizedEmail,password})
    pendingAttemptRef.current = attempt
    const finished = () => {
      if (pendingAttemptRef.current !== attempt) return
      pendingAttemptRef.current = null
      if (current()) setAttemptPending(false)
    }
    // The SDK may outlive the UI deadline (for example a storage lock). Keep
    // the real request guard until completion, preventing concurrent attempts.
    attempt.completion.then(finished,finished)
    try {
      const session = await attempt.result
      if (!current()) return
      if(expectedEmail&&String(session.user?.email||'').toLowerCase()!==expectedEmail.toLowerCase())throw new Error(t.loginUnexpectedError)
      const supportLogin = staffSupportEnabled(window.location.hostname, isNativeApp()) && (supportIntent || isSupportAccount(session.user))
      const supportTarget=supportReturnScreen==='support-app'?'support-app':'support'
      if (supportLogin) setScreen(supportTarget)
      setUser(session.user)
      window.history.replaceState({}, '', preserveForumTarget(supportLogin ? supportLoginPath(window.location.pathname,supportTarget) : window.location.pathname))

      if (!supportLogin && !isNativeApp() && session.user.id) {
        const userId = session.user.id
        Promise.all([
          supabase.from('companies').select('name, city, canton').eq('user_id', userId).maybeSingle(),
          supabase.from('subscriptions').select('plan, status').eq('user_id', userId).maybeSingle(),
        ]).then(([{ data: companyData }, { data: subscriptionData }]) => {
          notifyTelegramActivity('login', {
            email: session.user.email || normalizedEmail,
            company: companyData?.name,
            city: companyData?.city,
            canton: companyData?.canton,
            plan: subscriptionData?.plan || 'Starter',
            status: subscriptionData?.status,
          }, { cooldownMinutes: 0 })
        }).catch(error => {
          console.warn('Login activity notification failed:', error)
        })
      }
    } catch (error) {
      if (!current()) return
      setError(error?.code === 'LOGIN_ATTEMPT_PENDING' ? t.loginAttemptPending
        : error?.code === 'LOGIN_SESSION_MISSING' ? t.loginSessionMissing
        : isLoginServiceError(error) ? t.loginServiceUnavailable
          : typeof error?.message === 'string' && error.message.trim() ? error.message : t.loginUnexpectedError)
    } finally {
      if (current()) setLoading(false)
    }
  }

  const handleRepairLogin = async () => {
    if (repairing) return
    setRepairing(true)
    await repairLocalBrowserSession()
    window.history.replaceState({}, '', supportIntent ? supportLoginPath(window.location.pathname) : window.location.pathname)
    window.location.reload()
  }

  return (
    <div style={{height:'100dvh',display:'flex',flexDirection:'column',padding:'calc(env(safe-area-inset-top) + 1rem) 2rem calc(env(safe-area-inset-bottom) + 2rem)',gap:'1rem',background:'white',overflowY:'auto',overflowX:'hidden',WebkitOverflowScrolling:'touch'}}>
      <button onClick={() => setScreen('home')} style={{background:'none',border:'none',cursor:'pointer',color:'#666',textAlign:'left',fontSize:14,alignSelf:'flex-start',padding:'0.25rem 0',marginBottom:'0.5rem'}}>
        {t.back}
      </button>
      <h2 style={{fontSize:24,fontWeight:700,marginBottom:'0.5rem'}}>{supportIntent ? 'Connexion Support Hubbing' : t.loginTitle}</h2>
      {expectedEmail&&<p>Compte : <strong>{expectedEmail}</strong></p>}
      <input value={expectedEmail||email} readOnly={!!expectedEmail} onChange={e => setEmail(e.target.value)} placeholder={t.email} type="email" inputMode="email" autoComplete="username"
        style={{padding:'14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
      <div style={{position:'relative'}}>
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder={t.password} type={showPassword ? 'text' : 'password'} autoComplete="current-password"
          style={{width:'100%',padding:'14px 96px 14px 14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
        <button type="button" onClick={() => setShowPassword(value => !value)}
          aria-label={showPassword ? t.hidePassword : t.showPassword}
          style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',border:'none',background:'#f5f5f5',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:700,color:'#666',cursor:'pointer',fontFamily:'Plus Jakarta Sans'}}>
          {showPassword ? t.hidePassword : t.showPassword}
        </button>
      </div>
      {error && <p role="alert" style={{color:'#E24B4A',fontSize:13}}>{error}</p>}
      {attemptPending && !loading && error !== t.loginAttemptPending && <p role="status" style={{color:'#6B7280',fontSize:12}}>{t.loginAttemptPending}</p>}
      <button onClick={handleLogin} disabled={loading || attemptPending}
        style={{padding:'14px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:16,fontWeight:600,cursor:'pointer'}}>
        {loading ? t.connecting : attemptPending ? t.loginWaiting : t.loginBtn}
      </button>
      <button type="button" onClick={() => setScreen('forgot-password')}
        style={{background:'none',border:'none',cursor:'pointer',color:'#E24B4A',fontSize:14,fontWeight:700,fontFamily:'Plus Jakarta Sans',alignSelf:'center'}}>
        {t.forgotPassword}
      </button>
      {showRepairLogin && (
        <button type="button" onClick={handleRepairLogin} disabled={repairing}
          style={{background:'none',border:'none',cursor:'pointer',color:'#6B7280',fontSize:12,fontWeight:700,fontFamily:'Plus Jakarta Sans',alignSelf:'center'}}>
          {repairing ? t.repairingLogin : t.repairLogin}
        </button>
      )}
      {!supportIntent && <p style={{textAlign:'center',fontSize:14,color:'#666'}}>
        {t.noAccount} <span onClick={() => setScreen('register')} style={{color:'#E24B4A',cursor:'pointer',fontWeight:600}}>{t.register}</span>
      </p>}
      <button onClick={() => setScreen('legal')}
        style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:'#bbb',textDecoration:'underline',marginTop:'auto'}}>
        {t.legal}
      </button>
    </div>
  )
}

function ForgotPasswordScreen({ setScreen, t, supportDestination = false }) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleResetRequest = async () => {
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) {
      setError(t.errorFields)
      return
    }

    setLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: getPasswordResetRedirectUrl(),
    })

    if (error) {
      setError(error.message)
    } else {
      setSent(true)
    }
    setLoading(false)
  }

  return (
    <div style={{height:'100dvh',display:'flex',flexDirection:'column',padding:'calc(env(safe-area-inset-top) + 1rem) 2rem calc(env(safe-area-inset-bottom) + 2rem)',gap:'1rem',background:'white',overflowY:'auto',overflowX:'hidden',WebkitOverflowScrolling:'touch'}}>
      <button onClick={() => setScreen('login')} style={{background:'none',border:'none',cursor:'pointer',color:'#666',textAlign:'left',fontSize:14,alignSelf:'flex-start',padding:'0.25rem 0',marginBottom:'0.5rem'}}>
        {t.back}
      </button>
      <h2 style={{fontSize:24,fontWeight:700,marginBottom:'0.25rem'}}>{t.resetPasswordTitle}</h2>
      <p style={{fontSize:14,lineHeight:1.55,color:'#777',marginBottom:'0.5rem'}}>{t.resetPasswordIntro}</p>
      <input value={email} onChange={e => setEmail(e.target.value)} placeholder={t.email} type="email" inputMode="email" autoComplete="username"
        style={{padding:'14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
      {error && <p style={{color:'#E24B4A',fontSize:13,lineHeight:1.4}}>{error}</p>}
      {sent && <p style={{color:'#3BA75C',fontSize:13,lineHeight:1.5,background:'#f0fff4',border:'1px solid #bce7c7',borderRadius:10,padding:'12px'}}>{t.resetPasswordEmailSent}</p>}
      <button onClick={handleResetRequest} disabled={loading || sent}
        style={{padding:'14px',background:sent ? '#9ad3aa' : '#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:16,fontWeight:600,cursor:sent ? 'default' : 'pointer'}}>
        {loading ? t.sendingResetLink : t.sendResetLink}
      </button>
      <button type="button" onClick={() => setScreen('login')}
        style={{background:'none',border:'none',cursor:'pointer',color:'#666',fontSize:14,fontWeight:700,fontFamily:'Plus Jakarta Sans',alignSelf:'center'}}>
        {t.backToLogin}
      </button>
      <button onClick={() => setScreen('legal')}
        style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:'#bbb',textDecoration:'underline',marginTop:'auto'}}>
        {t.legal}
      </button>
    </div>
  )
}

export function ResetPasswordScreen({ setScreen, t, supportDestination = false, user }) {
  const protectedRecovery=staffSupportEnabled(window.location.hostname,isNativeApp())
  const proofRef=useRef(protectedRecovery&&typeof getPasswordRecoveryProof==='function'?getPasswordRecoveryProof():{kind:'absent'})
  const [password,setPassword]=useState(''),[confirmPassword,setConfirmPassword]=useState('')
  const [showPassword,setShowPassword]=useState(false),[error,setError]=useState(''),[success,setSuccess]=useState(false)
  const [loading,setLoading]=useState(false),[checkingSession,setCheckingSession]=useState(true)
  const [recoveryVerified,setRecoveryVerified]=useState(!protectedRecovery),[recoveryEmail,setRecoveryEmail]=useState('')
  const resetMountedRef=useRef(false),resetUserRef=useRef(user?.id),resetOwnerRef=useRef(null),resetBusyRef=useRef(false),authorizationRef=useRef(null)
  resetUserRef.current=user?.id
  const invalidLink=proofRef.current?.kind==='invite'?(t.resetSupportInviteInvalid||t.resetLinkInvalid):t.resetLinkInvalid
  const verifyRecoveryIdentity=async()=>{
    const proof=proofRef.current
    if(proof?.kind!=='recovery'||typeof proof.accessToken!=='string'||!proof.accessToken)throw new Error(t.resetLinkInvalid)
    const [sessionResult,userResult]=await Promise.all([supabase.auth.getSession(),supabase.auth.getUser(proof.accessToken)])
    const session=sessionResult.data?.session,target=userResult.data?.user
    if(sessionResult.error||userResult.error||!target?.id||session?.access_token!==proof.accessToken||session?.user?.id!==target.id||
      (resetUserRef.current&&resetUserRef.current!==target.id))throw new Error(t.resetLinkInvalid)
    return {ownerId:target.id,accessToken:proof.accessToken,email:typeof target.email==='string'?target.email:''}
  }
  const bounded=async work=>{
    let timer
    try{return await Promise.race([work,new Promise((_,reject)=>{timer=window.setTimeout(()=>reject(new Error(t.resetLinkInvalid)),15000)})])}
    finally{window.clearTimeout(timer)}
  }
  useEffect(()=>{resetMountedRef.current=true;return()=>{resetMountedRef.current=false}},[])
  useEffect(()=>{
    const expected=authorizationRef.current?.ownerId||resetOwnerRef.current
    if((protectedRecovery||supportDestination)&&expected&&user?.id&&user.id!==expected){
      authorizationRef.current=null;proofRef.current={kind:'absent'};if(typeof clearPasswordRecoveryProof==='function')clearPasswordRecoveryProof();setRecoveryVerified(false);setPassword('');setConfirmPassword('');setError(t.resetLinkInvalid)
    }
  },[protectedRecovery,supportDestination,user?.id,t.resetLinkInvalid])
  useEffect(()=>{
    let active=true
    authorizationRef.current=null;setRecoveryVerified(!protectedRecovery);setCheckingSession(true)
    const check=async()=>{
      if(protectedRecovery){
        if(proofRef.current?.kind!=='recovery')throw new Error(invalidLink)
        const verified=await bounded(verifyRecoveryIdentity())
        if(!active)return
        if(resetUserRef.current&&resetUserRef.current!==verified.ownerId)throw new Error(t.resetLinkInvalid)
        authorizationRef.current=verified;resetOwnerRef.current=verified.ownerId;setRecoveryEmail(verified.email);setRecoveryVerified(true)
      }else{
        const {data,error:sessionError}=await supabase.auth.getSession()
        if(!active)return
        if(sessionError||!data?.session)throw new Error(t.resetLinkInvalid)
        resetOwnerRef.current=data.session.user?.id||null
      }
    }
    check().catch(()=>{if(active){authorizationRef.current=null;if(protectedRecovery&&proofRef.current?.kind==='recovery'){proofRef.current={kind:'absent'};if(typeof clearPasswordRecoveryProof==='function')clearPasswordRecoveryProof()}setRecoveryVerified(false);setError(invalidLink)}}).finally(()=>{if(active)setCheckingSession(false)})
    return()=>{active=false}
    // The proof is an immutable bootstrap capture; only translated errors can change this check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[protectedRecovery,t.resetLinkInvalid,invalidLink])

  const handlePasswordUpdate=async()=>{
    if(resetBusyRef.current||checkingSession||success)return
    if(protectedRecovery&&(!recoveryVerified||!authorizationRef.current)){setError(invalidLink);return}
    setError('')
    if(password.length<8){setError(t.passwordTooShort);return}
    if(password!==confirmPassword){setError(t.passwordMismatch);return}
    resetBusyRef.current=true;setLoading(true)
    const authorized=authorizationRef.current,expectedOwner=authorized?.ownerId||resetOwnerRef.current
    const current=()=>resetMountedRef.current&&(!(protectedRecovery||supportDestination)||!!expectedOwner&&(!resetUserRef.current||resetUserRef.current===expectedOwner))
    try{
      if(protectedRecovery){
        let checked
        try{checked=await bounded(verifyRecoveryIdentity())}catch{
          authorizationRef.current=null;setRecoveryVerified(false);throw new Error(t.resetLinkInvalid)
        }
        if(!current())return
        if(checked.ownerId!==expectedOwner||checked.accessToken!==authorized.accessToken)throw new Error(t.resetLinkInvalid)
        const controller=new AbortController()
        let timer
        try{
          const update=async()=>{
            const response=await fetch(`${SUPABASE_URL}/auth/v1/user`,{method:'PUT',headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${authorized.accessToken}`,'Content-Type':'application/json'},body:JSON.stringify({password}),signal:controller.signal})
            const result=await response.json()
            if(!response.ok||result?.id!==expectedOwner)throw new Error(t.resetLinkInvalid)
          }
          await Promise.race([update(),new Promise((_,reject)=>{timer=window.setTimeout(()=>{controller.abort();reject(new Error(t.resetLinkInvalid))},15000)})])
        }finally{window.clearTimeout(timer);controller.abort()}
        if(!resetMountedRef.current)return
        // This token-bound operation must never mutate or sign out the global SDK session.
        authorizationRef.current=null;proofRef.current={kind:'absent'};if(typeof clearPasswordRecoveryProof==='function')clearPasswordRecoveryProof();setRecoveryVerified(false)
      }else{
        const {error:updateError}=await supabase.auth.updateUser({password})
        if(!current())return
        if(updateError)throw updateError
        await signOutCurrentBrowser()
      }
      if(protectedRecovery?!resetMountedRef.current:!current())return
      window.history.replaceState({},'',!protectedRecovery&&supportDestination?`${window.location.pathname}?screen=login&next=support`:window.location.pathname)
      setError('');setPassword('');setConfirmPassword('');setSuccess(true)
    }catch(updateError){if(current()){if(protectedRecovery){authorizationRef.current=null;proofRef.current={kind:'absent'};if(typeof clearPasswordRecoveryProof==='function')clearPasswordRecoveryProof();setRecoveryVerified(false)}setError(protectedRecovery?t.resetLinkInvalid:typeof updateError?.message==='string'?updateError.message:t.resetLinkInvalid)}}
    finally{resetBusyRef.current=false;if(resetMountedRef.current)setLoading(false)}
  }

  return (
    <div style={{height:'100dvh',display:'flex',flexDirection:'column',padding:'calc(env(safe-area-inset-top) + 1rem) 2rem calc(env(safe-area-inset-bottom) + 2rem)',gap:'1rem',background:'white',overflowY:'auto',overflowX:'hidden',WebkitOverflowScrolling:'touch'}}>
      <button onClick={() => setScreen('login')} style={{background:'none',border:'none',cursor:'pointer',color:'#666',textAlign:'left',fontSize:14,alignSelf:'flex-start',padding:'0.25rem 0',marginBottom:'0.5rem'}}>
        {t.back}
      </button>
      <h2 style={{fontSize:24,fontWeight:700,marginBottom:'0.25rem'}}>{t.newPasswordTitle}</h2>
      <p style={{fontSize:14,lineHeight:1.55,color:'#777',marginBottom:'0.5rem'}}>{t.newPasswordIntro}</p>
      {protectedRecovery&&(recoveryVerified||success)&&recoveryEmail&&<p>{t.resetAccountLabel||'Compte à réinitialiser :'} <strong>{recoveryEmail}</strong></p>}
      <div style={{position:'relative'}}>
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder={t.newPassword} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
          disabled={success || checkingSession || (protectedRecovery && !recoveryVerified)}
          style={{width:'100%',padding:'14px 96px 14px 14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
        <button type="button" onClick={() => setShowPassword(value => !value)}
          aria-label={showPassword ? t.hidePassword : t.showPassword}
          style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',border:'none',background:'#f5f5f5',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:700,color:'#666',cursor:'pointer',fontFamily:'Plus Jakarta Sans'}}>
          {showPassword ? t.hidePassword : t.showPassword}
        </button>
      </div>
      <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder={t.confirmPassword} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
        disabled={success || checkingSession || (protectedRecovery && !recoveryVerified)}
        style={{padding:'14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
      {error && <p style={{color:'#E24B4A',fontSize:13,lineHeight:1.4}}>{error}</p>}
      {success && <p style={{color:'#3BA75C',fontSize:13,lineHeight:1.5,background:'#f0fff4',border:'1px solid #bce7c7',borderRadius:10,padding:'12px'}}>{t.passwordUpdated}</p>}
      {!success ? (
        <button onClick={handlePasswordUpdate} disabled={loading || checkingSession || (protectedRecovery && !recoveryVerified) || (!!error && error === t.resetLinkInvalid)}
          style={{padding:'14px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:16,fontWeight:600,cursor:'pointer'}}>
          {loading || checkingSession ? t.updatingPassword : t.updatePassword}
        </button>
      ) : (
        <button onClick={() => setScreen(protectedRecovery?(recoveryEmail.toLowerCase()===SUPPORT_ACCOUNT_EMAIL?'support-login':'app'):'login')}
          style={{padding:'14px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:16,fontWeight:600,cursor:'pointer'}}>
          {protectedRecovery?(t.resetContinue||'Continuer'):t.backToLogin}
        </button>
      )}
      <button onClick={() => setScreen('legal')}
        style={{background:'none',border:'none',cursor:'pointer',fontSize:12,color:'#bbb',textDecoration:'underline',marginTop:'auto'}}>
        {t.legal}
      </button>
    </div>
  )
}

export function RegisterScreen({ setScreen, onBackHome, t, lang = 'fr', initialSelection, onSelectionChange, onPostalRegistrationIntent }) {
  const ui = getUiText(lang)
  const lookupCopy = getCompanyLookupCopy(lang)
  const postalCopy = getPostalVerificationCopy(lang)
  const [registrationDraft] = useState(readRegistrationDraft)
  const [selectedPlan, setSelectedPlan] = useState(initialSelection?.plan ?? registrationDraft.requestedPlan ?? '')
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const requested = initialSelection?.period ?? (registrationDraft.requestedPlan ? registrationDraft.requestedPeriod : 'monthly')
    return subscriptionOffersEnabled && BILLING_PERIODS.includes(requested) ? requested : 'monthly'
  })
  const registrationScrollRef = useRef(null)
  const registrationBusyRef = useRef(false)
  useEffect(() => {
    if (registrationScrollRef.current) registrationScrollRef.current.scrollTop = 0
    onSelectionChange?.({ plan: selectedPlan, period: selectedPeriod })
  }, [selectedPlan, selectedPeriod, onSelectionChange])
  const [email, setEmail] = useState(registrationDraft.email || '')
  const [personalEmail, setPersonalEmail] = useState(!isNativeApp() && registrationDraft.verificationMethod === 'postal')
  const postalSelected = !isNativeApp() && personalEmail
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [company, setCompany] = useState(registrationDraft.company || '')
  const [sector, setSector] = useState(registrationDraft.sector || '')
  const [serviceTags, setServiceTags] = useState(parseServiceTags(registrationDraft.serviceTags))
  const [zefix, setZefix] = useState(registrationDraft.zefix || '')
  const [contactName, setContactName] = useState(registrationDraft.contactName || '')
  const [contactTitle, setContactTitle] = useState(registrationDraft.contactTitle || '')
  const [contactPhone, setContactPhone] = useState(registrationDraft.contactPhone || '')
  const [address, setAddress] = useState(registrationDraft.address || '')
  const [city, setCity] = useState(registrationDraft.city || '')
  const [canton, setCanton] = useState(registrationDraft.canton || '')
  const [npa, setNpa] = useState(registrationDraft.npa || '')
  const [accepted, setAccepted] = useState(Boolean(registrationDraft.accepted))
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
 const [zefixStatus, setZefixStatus] = useState('idle') // idle, checking, valid, verified, manual, invalid
const [zefixCompanyName, setZefixCompanyName] = useState('')
const [zefixVerification, setZefixVerification] = useState(null)
const [postalOfficialIdentity,setPostalOfficialIdentity] = useState(null)

useEffect(() => {
  try {
    window.sessionStorage.setItem(REGISTRATION_DRAFT_KEY, JSON.stringify({
      email,
      verificationMethod: postalSelected ? 'postal' : 'professional',
      company,
      sector,
      serviceTags,
      zefix,
      contactName,
      contactTitle,
      contactPhone,
      address,
      city,
      canton,
      npa,
      accepted,
      requestedPlan: selectedPlan,
      requestedPeriod: selectedPeriod,
    }))
  } catch {
    // Safari private windows can block sessionStorage; the form still works without draft restore.
  }
}, [email, company, sector, serviceTags, zefix, contactName, contactTitle, contactPhone, address, city, canton, npa, accepted, selectedPlan, selectedPeriod, postalSelected])

const updateCompanyIdentity = (fields) => {
  setCompany(fields.company)
  setZefix(fields.zefix)
  setAddress(fields.address)
  setNpa(fields.npa)
  setCity(fields.city)
  setCanton(fields.canton)
  // Autofill is a convenience, never evidence of account or representative verification.
  setZefixCompanyName('')
  setZefixVerification(null)
  setPostalOfficialIdentity(null)
  setZefixStatus('idle')
}

const verifyZefixForRegistration = async (clean) => {
  setZefixStatus('checking')
  setZefixCompanyName('')
  setZefixVerification(null)
  setPostalOfficialIdentity(null)

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/verify-zefix`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        zefix: clean,
        company,
        sector,
        email: email.trim().toLowerCase(),
        ...(postalSelected ? {verificationMethod:'postal'} : {}),
        address,
        npa,
        city,
        canton,
      })
    })

    const rawText = await response.text()
    let payload = null
    try {
      payload = rawText ? JSON.parse(rawText) : null
    } catch (parseError) {
      payload = null
    }

    if (response.status === 404 && payload?.reason === 'not_found') {
      setZefixStatus('invalid')
      return { blocked: true, message: t.errorZefixNotFound }
    }

    if (response.status === 422 && payload?.reason === 'inactive_company') {
      setZefixStatus('invalid')
      return { blocked: true, message: t.errorZefixInactive }
    }

    if (postalSelected && response.status===409 && payload?.reason==='postal_identity_mismatch') {
      const official=payload?.company || {}
      const location=official.address || {}
      const officialAddress=typeof location==='string'?location:[location.street,location.houseNumber,location.swissZipCode,location.town || official.legalSeat,location.canton].filter(Boolean).join(' ')
      setPostalOfficialIdentity({name:String(official.name || ''),address:officialAddress})
      setZefixStatus('idle')
      return {blocked:true,message:postalCopy.identityMismatch}
    }

    if (response.status === 409 && payload?.reason === 'company_identity_mismatch') {
      setZefixStatus('invalid')
      return { blocked: true, message: t.errorZefixMismatch }
    }

    if (postalSelected && response.ok && payload?.postalEligible === true && payload?.verificationMethod === 'postal' && payload?.source === 'zefix') {
      setZefixCompanyName(payload.company?.name || '')
      setZefixVerification(payload)
      setZefixStatus('postal')
      return {blocked:false,payload}
    }

    if (response.status === 409 && payload?.reason === 'email_domain_mismatch') {
      setZefixStatus('invalid')
      return { blocked: true, message: t.errorEmailCompanyMismatch }
    }

    if (payload?.requiresManualReview) {
      setZefixStatus('invalid')
      return { blocked: true, message: t.errorZefixMismatch }
    }

    // A personal address needs affirmative registry approval for the postal
    // route. A registry outage never becomes a permissive manual fallback.
    if (postalSelected) {
      setZefixStatus('idle')
      return {blocked:true,message:postalCopy.postalUnavailable}
    }

    if (response.ok && payload?.verified) {
      const officialName = payload.company?.name || ''
      setZefixCompanyName(officialName)
      setZefixVerification(payload)
      setZefixStatus('verified')
      return { blocked: false, payload }
    }

    if (payload?.fallbackToManual || !response.ok) {
      setZefixStatus('manual')
      return {
        blocked: false,
        payload: {
          verified: false,
          source: 'manual_fallback',
          reason: payload?.reason || 'verify_zefix_unavailable',
        }
      }
    }

    setZefixStatus('manual')
    return {
      blocked: false,
      payload: {
        verified: false,
        source: 'manual_fallback',
        reason: payload?.reason || 'unknown_zefix_response',
      }
    }
  } catch (error) {
    console.error('Zefix verification failed:', error)
    if (postalSelected) {setZefixStatus('idle');return {blocked:true,message:postalCopy.postalUnavailable}}
    setZefixStatus('manual')
    return {
      blocked: false,
      payload: {
        verified: false,
        source: 'manual_fallback',
        reason: 'verify_zefix_network_error',
      }
    }
  }
}

const handleRegister = async () => {
    if (registrationBusyRef.current) return
    registrationBusyRef.current=true
    let authCreated=false
    setLoading(true)
    setError('')

    try {
    const normalizedEmail = email.trim().toLowerCase()
    const clean = zefix.replace(/[^0-9]/g, '').trim()
    if (postalSelected && !isPersonalEmail(normalizedEmail)) {
      setError(postalCopy.personalRequired);setLoading(false);return
    }
    if (!postalSelected && isPersonalEmail(normalizedEmail)) {
      setError(t.errorEmail)
      setLoading(false)
      return
    }

if (!normalizedEmail || !password || !company || !sector || !zefix || !contactName || !contactTitle.trim() || !contactPhone || !address || !city || !canton || !npa) {      setError(t.errorFields)
      setLoading(false)
      return
    }
    if (!accepted) {
      setError(t.errorCGU)
      setLoading(false)
      return
    }
if (zefixStatus === 'invalid') {
  setError(t.errorZefix)
  setLoading(false)
  return
}
    if (clean.length === 9) {
  setZefixStatus('valid')
} else {
  setZefixStatus('idle')
  setError(t.errorZefix)
  setLoading(false)
  return
}

    try {
      const { data: ideAvailable, error: ideCheckError } = await supabase.rpc('hubbing_company_ide_available', {
        p_zefix: clean
      })
      if (ideCheckError) throw ideCheckError
      if (ideAvailable === false) {
        setError(t.errorIdeAlreadyUsed)
        setLoading(false)
        return
      }
    } catch (ideCheckError) {
      console.error('IDE availability check failed:', ideCheckError)
      setError(t.errorZefixRetry)
      setLoading(false)
      return
    }

    const zefixCheck = await verifyZefixForRegistration(clean)
    if (zefixCheck.blocked) {
      setError(zefixCheck.message || t.errorZefix)
      setLoading(false)
      return
    }

    if (postalSelected) onPostalRegistrationIntent?.(true)
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: { data: { requested_plan: selectedPlan || 'starter', requested_period: selectedPlan === 'starter' ? 'monthly' : selectedPeriod, ...(postalSelected ? {registration_verification_method:'postal'} : {}) } },
    })
    if (error) {
      if (postalSelected) onPostalRegistrationIntent?.(false)
      const alreadyUsed = /already|registered|exists|user/i.test(error.message || '')
      setError(alreadyUsed ? t.errorEmailAlreadyUsed : error.message)
      setLoading(false)
      return
    }
    if (Array.isArray(data?.user?.identities) && data.user.identities.length === 0) {
      if (postalSelected) onPostalRegistrationIntent?.(false)
      setError(t.errorEmailAlreadyUsed)
      setLoading(false)
      return
    }
    
    const userId = data?.user?.id || data?.session?.user?.id
    authCreated=Boolean(userId)
    const verificationPayload = zefixCheck.payload || zefixVerification
    const isPostal = postalSelected && verificationPayload?.postalEligible === true && verificationPayload?.verificationMethod === 'postal'
    const isZefixVerified = !isPostal && verificationPayload?.verified === true && verificationPayload?.source === 'zefix'

    if (userId) {
      let coords = null
      try {
        coords = await geocodeSwissAddress({ address, npa, city, canton })
      } catch (geoError) {
        console.log('Registration geocoding failed:', geoError)
      }
      const { error: insertError } = await supabase.from('companies').insert({
        user_id: userId,
      name: company,
      sector,
      service_tags: serviceTags,
      zefix_uid: clean,
      contact_name: contactName,
      contact_title: contactTitle,
      contact_phone: contactPhone,
      address: `${address}, ${npa} ${city}`,
     city: city,
     canton: canton,
     lat: coords?.lat ?? null,
     lng: coords?.lng ?? null,
      zefix_verification_status: isPostal ? 'postal_pending' : isZefixVerified ? 'verified' : 'manual_pending',
      zefix_verified_at: isZefixVerified ? new Date().toISOString() : null,
      zefix_verified_name: isZefixVerified ? (verificationPayload?.company?.name || company) : null,
      zefix_verified_source: isPostal ? 'postal' : verificationPayload?.source || null,
      zefix_verified_payload: verificationPayload || null,
      })
      if (insertError) {
        console.error('Insert error:', insertError)
        const duplicateIde = insertError.code === '23505' || /zefix|companies_zefix|duplicate/i.test(insertError.message || '')
        setError(isPostal && /postal_identity_mismatch/.test(insertError.message || '') ? postalCopy.identityMismatch : duplicateIde ? t.errorIdeAlreadyUsed : insertError.message)
        setLoading(false)
        return
      }
      if (isPostal) window.dispatchEvent(new CustomEvent('hubbing:companies-changed',{detail:{reason:'postal-registration'}}))
    }
    try {
      let registrationEmailToken=SUPABASE_ANON_KEY
      if (isPostal) {
        const {data:currentAuth,error:currentAuthError}=await supabase.auth.getSession()
        const currentSession=currentAuth?.session
        if (currentAuthError || currentSession?.user?.id!==userId || String(currentSession?.user?.email || '').toLowerCase()!==normalizedEmail || !currentSession.access_token) throw new Error('Registration notification session unavailable')
        registrationEmailToken=currentSession.access_token
      }
      const response = await fetch(`${SUPABASE_URL}/functions/v1/registration-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${registrationEmailToken}`
        },
        body: JSON.stringify({
          email: normalizedEmail,
          company,
          sector,
          zefix: clean,
          contactName,
          contactTitle,
          contactPhone,
          address: `${address}, ${npa} ${city}`,
          city,
          canton,
          userId,
          zefixVerification: verificationPayload,
          requestedPlan: selectedPlan || 'starter',
          requestedPeriod: selectedPlan === 'starter' ? 'monthly' : selectedPeriod
        })
      })
      if (!response.ok) {
        const details = await response.text()
        throw new Error(details || 'Registration email failed')
      }
    } catch (emailError) {
      console.error('Registration email error:', emailError)
    }

    clearRegistrationDraft()
    setSuccess(true)
    setLoading(false)
    } catch (failure) {
      if (postalSelected && !authCreated) onPostalRegistrationIntent?.(false)
      setError(isLoginServiceError(failure) ? t.loginServiceUnavailable
        : typeof failure?.message==='string' && failure.message.trim() ? failure.message : t.loginUnexpectedError)
    } finally {
      registrationBusyRef.current=false
      setLoading(false)
    }
  }

  if (success) return (
    <div style={{height:'100dvh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'calc(env(safe-area-inset-top) + 1rem) 2rem calc(env(safe-area-inset-bottom) + 2rem)',gap:'1rem',textAlign:'center',background:'white'}}>
      <HubbingIcon name="sparkles" size={48} color="#E24B4A" />
      <h2 style={{fontSize:22,fontWeight:700}}>{t.successTitle}</h2>
      <p style={{color:'#666',fontSize:15}}>{postalSelected ? postalCopy.signup : t.successMsg}</p>
      <button onClick={() => setScreen('login')}
        style={{padding:'14px 32px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:15,fontWeight:600,cursor:'pointer'}}>
        {t.successBtn}
      </button>
    </div>
  )

  if (!selectedPlan && subscriptionOffersEnabled) return (
    <div style={{height:'100dvh',paddingTop:'env(safe-area-inset-top)',paddingBottom:'env(safe-area-inset-bottom)',background:'#F8FAFC',display:'flex',flexDirection:'column'}}>
    <div ref={registrationScrollRef} style={{flex:1,overflowY:'auto',padding:'1rem 1rem 2rem'}}>
      <button type="button" onClick={onBackHome} style={{border:0,background:'none',padding:'12px 0',cursor:'pointer'}}>{t.back}</button>
      <Suspense fallback={<LoadingIndicator />}><RegistrationOffers lang={lang} initialPeriod={selectedPeriod}
        onChoose={(plan, period) => { setSelectedPeriod(period); setSelectedPlan(plan) }} /></Suspense>
    </div></div>
  )

  if (!selectedPlan) {
    const planCopy = {
      fr: {
        title: 'Choisissez votre forfait',
        subtitle: 'Comparez les fonctions avant de créer votre compte. Les forfaits payants seront activés après validation et paiement sécurisé.',
        choose: 'Choisir ce forfait',
        recommended: 'Recommandé',
      },
      de: {
        title: 'Wählen Sie Ihr Abo',
        subtitle: 'Vergleichen Sie die Funktionen vor der Kontoerstellung. Kostenpflichtige Abos werden nach Prüfung und sicherer Zahlung aktiviert.',
        choose: 'Dieses Abo wählen',
        recommended: 'Empfohlen',
      },
      it: {
        title: 'Scegli il tuo piano',
        subtitle: 'Confronta le funzioni prima di creare l’account. I piani a pagamento saranno attivati dopo la verifica e il pagamento sicuro.',
        choose: 'Scegli questo piano',
        recommended: 'Consigliato',
      },
      en: {
        title: 'Choose your plan',
        subtitle: 'Compare features before creating your account. Paid plans are activated after approval and secure payment.',
        choose: 'Choose this plan',
        recommended: 'Recommended',
      },
    }[lang] || {}
    const plans = [
      { id:'starter', name:'Starter', price:ui.common.free, color:'#64748B', features:ui.pricing.starterFeatures, limits:ui.pricing.starterLimits },
      { id:'basic', name:'Basic', price:`CHF 19.-${ui.common.month}`, color:'#185FA5', features:ui.pricing.basicFeatures, limits:ui.pricing.basicLimits },
      { id:'premium', name:'Premium', price:`CHF 39.-${ui.common.month}`, color:'#E24B4A', features:ui.pricing.premiumFeatures, limits:ui.pricing.premiumLimits, recommended:true },
    ]
    return (
      <div ref={registrationScrollRef} style={{height:'100dvh',overflowY:'auto',background:'#F8FAFC',padding:'calc(env(safe-area-inset-top) + 1rem) 1rem calc(env(safe-area-inset-bottom) + 2rem)',WebkitOverflowScrolling:'touch'}}>
        <div style={{width:'100%',maxWidth:430,margin:'0 auto'}}>
          <button type="button" onClick={onBackHome}
            style={{background:'none',border:'none',cursor:'pointer',color:'#64748B',fontSize:14,fontWeight:700,padding:'0.25rem 0 1rem'}}>
            {t.back}
          </button>
          <h1 style={{fontSize:25,fontWeight:900,color:'#111827',margin:'0 0 7px'}}>{planCopy.title}</h1>
          <p style={{fontSize:13,color:'#64748B',lineHeight:1.55,margin:'0 0 18px'}}>{planCopy.subtitle}</p>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {plans.map(planOption => (
              <section key={planOption.id}
                style={{background:'white',border:planOption.recommended ? `2px solid ${planOption.color}` : '1px solid #E2E8F0',borderRadius:16,padding:'1rem',boxShadow:'0 8px 24px rgba(15,23,42,0.06)'}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:10}}>
                  <div>
                    <h2 style={{fontSize:18,fontWeight:900,color:planOption.color,margin:0,display:'flex',alignItems:'center',gap:6}}>
                      {planOption.name}<VerifiedBadge size={17} variant={planOption.id} />
                    </h2>
                    {planOption.recommended && <span style={{fontSize:10,fontWeight:900,color:'#E24B4A'}}>{planCopy.recommended}</span>}
                  </div>
                  <strong style={{fontSize:15,color:'#111827'}}>{planOption.price}</strong>
                </div>
                {planOption.features.slice(0, 7).map(feature => (
                  <p key={feature} style={{fontSize:12,color:'#475569',margin:'5px 0',lineHeight:1.4}}>✓ {feature}</p>
                ))}
                {planOption.limits.slice(0, 3).map(limit => (
                  <p key={limit} style={{fontSize:12,color:'#94A3B8',margin:'5px 0',lineHeight:1.4}}>− {limit}</p>
                ))}
                <button type="button" onClick={() => setSelectedPlan(planOption.id)}
                  style={{width:'100%',marginTop:12,padding:'12px',background:planOption.color,color:'white',border:'none',borderRadius:11,fontSize:14,fontWeight:800,fontFamily:'Plus Jakarta Sans'}}>
                  {planCopy.choose}
                </button>
              </section>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={registrationScrollRef} style={{height:'100dvh',display:'flex',flexDirection:'column',padding:'calc(env(safe-area-inset-top) + 1rem) 2rem calc(env(safe-area-inset-bottom) + 2.5rem)',gap:'1rem',overflowY:'auto',overflowX:'hidden',WebkitOverflowScrolling:'touch',background:'white'}}>
      <div className="registration-navigation">
        <button type="button" onClick={onBackHome} disabled={loading} className="registration-navigation__button">
          <span aria-hidden="true"><HubbingIcon name="home" size={16} color="currentColor" /></span>
          {getBillingCopy(lang).backHome}
        </button>
        <button type="button" onClick={() => setSelectedPlan('')} disabled={loading} className="registration-navigation__button registration-navigation__button--plans">
          <span aria-hidden="true"><HubbingIcon name="pricing" size={16} color="currentColor" /></span>
          {getBillingCopy(lang).changePlan}
        </button>
      </div>
      <h2 style={{fontSize:24,fontWeight:700}}>{t.registerTitle}</h2>
      <p style={{color:'#666',fontSize:13}}>{t.registerSubtitle}</p>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:12,padding:'10px 12px'}}>
        <span style={{fontSize:13,color:'#64748B'}}>{({fr:'Forfait choisi',de:'Gewähltes Abo',it:'Piano scelto',en:'Selected plan'}[lang] || ui.common.currentPlan)}</span>
        <strong style={{fontSize:14,color:selectedPlan === 'premium' ? '#E24B4A' : selectedPlan === 'basic' ? '#185FA5' : '#64748B',textTransform:'capitalize'}}>{selectedPlan}{subscriptionOffersEnabled && selectedPlan !== 'starter' ? ` · ${getBillingCopy(lang)[selectedPeriod]}` : ''}</strong>
      </div>

      <p style={{fontSize:12,color:'#E24B4A',fontWeight:600,marginTop:'0.5rem'}}>{t.companyInfo}</p>
      <CompanyLookup value={{ company, zefix, address, npa, city, canton }} onChange={updateCompanyIdentity}
        lookup={lookupZefix} lang={lang} disabled={loading} allowSearch={process.env.REACT_APP_COMPANY_LOOKUP_ENABLED === 'true'} />
      <p style={{fontSize:12,color:'#64748B',lineHeight:1.5,margin:0}}>{lookupCopy.sectorHint}</p>
      <div style={{position:'relative',width:'100%',height:56,flexShrink:0}}>
        <div style={{
          position:'absolute',
          inset:0,
          padding:'0 44px 0 14px',
          border:'1px solid #ddd',
          borderRadius:10,
          background:'white',
          display:'flex',
          alignItems:'center',
          fontSize:16,
          lineHeight:1.2,
          fontFamily:'Plus Jakarta Sans',
          color:sector ? '#111' : '#999',
          pointerEvents:'none',
          overflow:'hidden',
          textOverflow:'ellipsis',
          whiteSpace:'nowrap'
        }}>
          {sector || t.sector}
        </div>
        <span style={{position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',fontSize:18,color:'#111',pointerEvents:'none'}}>⌄</span>
        <select value={sector} onChange={e => setSector(e.target.value)} aria-label={t.sector}
          style={{position:'absolute',inset:0,width:'100%',height:'100%',opacity:0,cursor:'pointer',fontSize:16}}>
          <option value="">{t.sector}</option>
          {COMPANY_SECTORS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
      <div style={{background:'#FFF9F9',border:'1px solid #F5D0D0',borderRadius:12,padding:'12px'}}>
        <ServiceTagsEditor value={serviceTags} onChange={setServiceTags} text={ui.profile.serviceTags} compact />
      </div>
      {zefixStatus === 'checking' && <p style={{fontSize:12,color:'#94600b'}}>{t.zefixChecking}</p>}
      {zefixStatus === 'verified' && <p style={{fontSize:12,color:'#16a34a'}}>{t.zefixVerified(zefixCompanyName)}</p>}
      {postalOfficialIdentity && <div style={{padding:12,border:'1px solid #FDE68A',borderRadius:10,background:'#FFFBEB',color:'#92400E',fontSize:12,lineHeight:1.5}}>
        {postalOfficialIdentity.name && <p style={{margin:'0 0 4px'}}><strong>{postalCopy.officialName} : </strong>{postalOfficialIdentity.name}</p>}
        {postalOfficialIdentity.address && <p style={{margin:0}}><strong>{postalCopy.officialAddress} : </strong>{postalOfficialIdentity.address}</p>}
      </div>}
      {zefixStatus === 'postal' && <p style={{fontSize:12,color:'#92400E'}}>{postalCopy.pending}</p>}
      {zefixStatus === 'manual' && <p style={{fontSize:12,color:'#94600b'}}>{t.zefixManualFallback}</p>}

      <DecisionMakerFields value={{ contactName, contactTitle, contactPhone }} lang={lang} disabled={loading}
        onChange={fields => { setContactName(fields.contactName); setContactTitle(fields.contactTitle); setContactPhone(fields.contactPhone) }} />

      <p style={{fontSize:12,color:'#E24B4A',fontWeight:600,marginTop:'0.25rem'}}>{t.access}</p>
      <RegistrationEmailChoice personal={postalSelected} native={isNativeApp()} disabled={loading} lang={lang} onChange={value=>{setPersonalEmail(value);setZefixStatus('idle');setZefixVerification(null);setPostalOfficialIdentity(null);setError('')}} />
      <input value={email} onChange={e => {setEmail(e.target.value);setZefixStatus('idle');setZefixVerification(null);setPostalOfficialIdentity(null)}} placeholder={postalSelected ? postalCopy.personalEmail : t.email} disabled={loading} type="email" inputMode="email" autoComplete="email"
        style={{padding:'14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
      <div style={{position:'relative'}}>
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder={t.password} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
          style={{width:'100%',padding:'14px 96px 14px 14px',border:'1px solid #ddd',borderRadius:10,fontSize:16,outline:'none'}} />
        <button type="button" onClick={() => setShowPassword(value => !value)}
          aria-label={showPassword ? t.hidePassword : t.showPassword}
          style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',border:'none',background:'#f5f5f5',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:700,color:'#666',cursor:'pointer',fontFamily:'Plus Jakarta Sans'}}>
          {showPassword ? t.hidePassword : t.showPassword}
        </button>
      </div>

      <div style={{display:'flex',alignItems:'flex-start',gap:10,marginTop:'0.25rem'}}>
        <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)}
          style={{marginTop:3,cursor:'pointer',width:16,height:16,flexShrink:0}} />
        <p style={{fontSize:13,color:'#666',lineHeight:1.5}}>
          {t.acceptCGU} <span onClick={() => setScreen('legal')} style={{color:'#E24B4A',cursor:'pointer',textDecoration:'underline'}}>{t.cgu}</span> {t.and} <span onClick={() => setScreen('legal')} style={{color:'#E24B4A',cursor:'pointer',textDecoration:'underline'}}>{t.privacy}</span> {t.of}
        </p>
      </div>

      {error && <p style={{color:'#E24B4A',fontSize:13}}>{error}</p>}
      <button onClick={handleRegister} disabled={loading}
        style={{padding:'14px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:16,fontWeight:600,cursor:'pointer',marginTop:'0.5rem'}}>
        {loading ? t.creating : t.createBtn}
      </button>
    </div>
  )
}

function VisitorMode({ setScreen, initialTab = 'swipe', t, lang, setLang }) {
  useEffect(() => startCompanyDataSync(supabase, 'visitor'), [])
  const [activeTab, setActiveTab] = useState(initialTab)
  const [showModal, setShowModal] = useState(false)
  const [showLangMenu, setShowLangMenu] = useState(false)
  const ui = getUiText(lang)

  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  const tabStyle = (tab) => ({
    flex:1, padding:'8px 0 6px', background:'none', border:'none', cursor:'pointer',
    fontSize:11, color: activeTab === tab ? '#E24B4A' : '#6B7280',
    fontWeight: activeTab === tab ? 700 : 500,
    fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  })

  const Modal = () => (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:'1rem'}}>
      <div style={{background:'white',borderRadius:16,padding:'2rem',width:'100%',maxWidth:340,textAlign:'center'}}>
        <div style={{marginBottom:'0.75rem',display:'flex',justifyContent:'center'}}>
          <HubbingIcon name="lock" size={40} color="#E24B4A" />
        </div>
        <h3 style={{fontSize:18,fontWeight:700,marginBottom:8}}>{t.lockTitle}</h3>
        <p style={{fontSize:14,color:'#666',lineHeight:1.6,marginBottom:'1.25rem'}}>{t.lockDesc}</p>
        <button onClick={() => setScreen('register')}
          style={{width:'100%',padding:'13px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:15,fontWeight:600,cursor:'pointer',marginBottom:'0.75rem'}}>
          {t.createFree}
        </button>
        <button onClick={() => setScreen('login')}
          style={{width:'100%',padding:'13px',background:'white',color:'#E24B4A',border:'2px solid #E24B4A',borderRadius:12,fontSize:15,fontWeight:600,cursor:'pointer',marginBottom:'0.75rem'}}>
          {t.login}
        </button>
        <button onClick={() => setShowModal(false)}
          style={{background:'none',border:'none',cursor:'pointer',fontSize:13,color:'#999'}}>
          {t.continueDemo}
        </button>
      </div>
    </div>
  )

  return (
    <div style={{
  height:'100dvh',
  display:'flex',
  flexDirection:'column',
  overflow:'hidden',
  background:'white'
}}>
      {showModal && <Modal />}

      <div style={{
  padding:'calc(env(safe-area-inset-top) + 0.55rem) 1rem 0.55rem',
  borderBottom:'1px solid #f0f0f0',
  display:'flex',
  alignItems:'center',
  justifyContent:'space-between',
  flexShrink:0,
  background:'white',
  zIndex:20
}}>
        <div onClick={() => setScreen('home')} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
  <img src="/LOGO-HUBBING.svg" alt="Hubbing" style={{width:32,height:32,borderRadius:8}} />
  <span style={{fontWeight:700,fontSize:16}}>Hubbing</span>
</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <div style={{position:'relative'}}>
            <button onClick={() => setShowLangMenu(!showLangMenu)}
              style={{background:'#f5f5f5',border:'1px solid #eee',borderRadius:20,padding:'5px 10px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
              {lang.toUpperCase()} ▾
            </button>
            {showLangMenu && (
              <div style={{position:'absolute',right:0,top:'110%',background:'white',border:'1px solid #eee',borderRadius:12,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',overflow:'hidden',zIndex:100,minWidth:120}}>
                {[{code:'fr',label:'Français'},{code:'de',label:'Deutsch'},{code:'it',label:'Italiano'},{code:'en',label:'English'}].map(l => (
                  <button key={l.code} onClick={() => { setLang(l.code); setShowLangMenu(false) }}
                    style={{display:'block',width:'100%',padding:'8px 16px',background: lang === l.code ? '#FFF5F5' : 'white',border:'none',cursor:'pointer',fontSize:13,textAlign:'left',color: lang === l.code ? '#E24B4A' : '#333',fontWeight: lang === l.code ? 600 : 400}}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setScreen('register')}
            style={{background:'#E24B4A',color:'white',border:'none',borderRadius:20,padding:'6px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
            {t.createAccount}
          </button>
          <button onClick={() => setScreen('login')}
            style={{background:'white',color:'#E24B4A',border:'1px solid #E24B4A',borderRadius:20,padding:'6px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
            {t.login}
          </button>
        </div>
      </div>

      <div style={{
  flex:1,
  minHeight:0,
  display:'flex',
  flexDirection:'column',
  overflowY:'auto',
  overflowX:'hidden',
  WebkitOverflowScrolling:'touch',
  paddingBottom:'calc(60px + env(safe-area-inset-bottom))'
}}>
        {activeTab === 'swipe' && <SwipeScreen user={null} setScreen={setScreen} lang={lang} />}
       {activeTab === 'map' && <Suspense fallback={<LoadingIndicator fill compact />}><MapScreen user={null} setScreen={setScreen} lang={lang} /></Suspense>}
        {activeTab === 'messages' && (
          <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'2rem',textAlign:'center',gap:'1rem'}}>
            <HubbingIcon name="messages" size={48} color="#E24B4A" />
            <h3 style={{fontSize:18,fontWeight:700}}>{t.messagesTitle}</h3>
            <p style={{color:'#999',fontSize:14,lineHeight:1.6}}>{t.messagesDesc}</p>
            <button onClick={() => setShowModal(true)}
              style={{padding:'12px 24px',background:'#E24B4A',color:'white',border:'none',borderRadius:12,fontSize:14,fontWeight:600,cursor:'pointer'}}>
              {t.accessMessages}
            </button>
          </div>
        )}
        {activeTab === 'pricing' && (
          <div style={{flex:1,overflowY:'auto',padding:'1.5rem 1rem'}}>
            <div style={{textAlign:'center',marginBottom:'1.25rem'}}>
              <h2 style={{fontSize:20,fontWeight:700,marginBottom:4}}>{t.pricing}</h2>
              <p style={{fontSize:13,color:'#666'}}>{t.pricingDesc}</p>
            </div>
            {[
              {id:'starter',name:'Starter',price:ui.common.free,color:'#666',features:ui.pricing.starterFeatures.slice(0, 4),limits:ui.pricing.starterLimits.slice(0, 2)},
              {id:'basic',name:'Basic',price:`CHF 19.-${ui.common.month}`,color:'#185FA5',features:ui.pricing.basicFeatures.slice(0, 4),limits:ui.pricing.basicLimits.slice(0, 2)},
              {id:'premium',name:'Premium',price:`CHF 39.-${ui.common.month}`,color:'#E24B4A',features:ui.pricing.premiumFeatures.slice(0, 6),limits:ui.pricing.premiumLimits,highlight:true},
            ].map(plan => (
              <div key={plan.name} onClick={() => setShowModal(true)}
                style={{border: plan.highlight ? `2px solid ${plan.color}` : '1px solid #eee',borderRadius:12,padding:'1rem',marginBottom:'0.75rem',cursor:'pointer',background:'white'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <h3 style={{fontSize:16,fontWeight:700,color:plan.color,margin:0,display:'flex',alignItems:'center',gap:6}}>
                    {plan.name}
                    <VerifiedBadge size={17} variant={plan.id} />
                  </h3>
                  <span style={{fontSize:14,fontWeight:600,color:'#1a1a1a'}}>{plan.price}</span>
                </div>
                {plan.features.map((f,i) => (
                  <p key={i} style={{fontSize:12,color:'#666',margin:'3px 0',display:'flex',alignItems:'center',gap:5}}>
                    <span>✓ {f}</span>
                  </p>
                ))}
                {(plan.limits || []).map((f,i) => (
                  <p key={`limit-${i}`} style={{fontSize:12,color:'#999',margin:'3px 0'}}>− {f}</p>
                ))}
              </div>
            ))}
            <div style={{marginTop:'0.85rem',padding:'0.9rem',background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:12,textAlign:'center'}}>
              <p style={{fontSize:12,fontWeight:700,color:'#334155',margin:'0 0 0.6rem'}}>
                {ui.pricing.legalLinksTitle}
              </p>
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,flexWrap:'wrap'}}>
                <a href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:12,color:'#185FA5',fontWeight:700,textDecoration:'underline'}}>
                  {ui.pricing.termsOfUseEula}
                </a>
                <span style={{fontSize:12,color:'#CBD5E1'}}>·</span>
                <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:12,color:'#185FA5',fontWeight:700,textDecoration:'underline'}}>
                  {ui.pricing.privacyPolicyLink}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{
  position:'fixed',
  left:'50%',
  bottom:0,
  transform:'translateX(-50%)',
  width:'100%',
  maxWidth:430,
  borderTop:'1px solid #f0f0f0',
  display:'flex',
  background:'white',
  paddingBottom:'env(safe-area-inset-bottom)',
  flexShrink:0,
  zIndex:9999
}}>
        {[
          {id:'swipe',label:ui.nav.swipe,icon:'swipe'},
          {id:'map',label:ui.nav.map,icon:'map'},
          {id:'messages',label:ui.nav.messages,icon:'messages'},
          {id:'pricing',label:ui.nav.pricing,icon:'pricing'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={tabStyle(tab.id)}>
            <div style={{height:26,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:2}}>
              <HubbingIcon name={tab.icon} active={activeTab === tab.id} />
            </div>
            <span style={{display:'block'}}>{tab.label}</span>
            <span style={{display:'block',width:4,height:4,borderRadius:'50%',background:activeTab === tab.id ? '#E24B4A' : 'transparent',margin:'4px auto 0'}} />
          </button>
        ))}
      </div>
    </div>
  )
}
 

export function SupportObserverAccess({user,onBack,children}) {
  const [access,setAccess]=useState(null),[retry,setRetry]=useState(0)
  const owner=useRef(user.id)
  owner.current=user.id
  useEffect(()=>{
    let active=true,inflight=false,controller
    const id=user.id
    setAccess(null)
    const check=async()=>{
      if(!active||inflight||document.hidden)return
      inflight=true;controller=new AbortController()
      try{
        const data=await readSupportAccess({expectedUserId:id,signal:controller.signal})
        if(active&&owner.current===id)setAccess({userId:id,allowed:data?.isStaff===true,error:false})
      }catch{if(active&&owner.current===id)setAccess({userId:id,allowed:false,error:true})}
      finally{inflight=false}
    }
    const foreground=()=>{if(!document.hidden)check()}
    check();window.addEventListener('focus',foreground);document.addEventListener('visibilitychange',foreground)
    return()=>{active=false;controller?.abort();window.removeEventListener('focus',foreground);document.removeEventListener('visibilitychange',foreground)}
  },[user.id,retry])
  if(access?.userId===user.id&&access.allowed)return children
  if(!access||access.userId!==user.id)return <LoadingIndicator fullScreen background="white"/>
  return <section style={{minHeight:'100dvh',display:'grid',alignContent:'center',gap:16,padding:24,background:'white'}}><h1>Accès à l’observation Hubbing</h1><p>{access.error?'Impossible de vérifier les droits. Réessayez.':'Ce compte ne dispose pas de l’accès Support Hubbing.'}</p>{access.error&&<button onClick={()=>setRetry(value=>value+1)}>Réessayer</button>}<button onClick={onBack}>Espace support</button></section>
}

export function Dashboard({ user, setUser, t, lang, setLang, initialTab = 'home', initialCompanyId = null, onCompanyLinkOpened, onOpenAdmin, isSupportObserver = false, onOpenSupport }) {
  const supportMessagesEnabled = !isSupportObserver && memberSupportEnabled(window.location.hostname, isNativeApp())
  const messageNotificationTypes = useMemo(() => supportMessagesEnabled ? [...MESSAGE_NOTIFICATION_TYPES, SUPPORT_NOTIFICATION_TYPE] : MESSAGE_NOTIFICATION_TYPES, [supportMessagesEnabled])
  const forumAvailable = forumEnabled()
  const appNotificationTypes = useMemo(() => [...(supportMessagesEnabled ? [...APP_NOTIFICATION_TYPES, SUPPORT_NOTIFICATION_TYPE] : APP_NOTIFICATION_TYPES),...(forumAvailable ? FORUM_NOTIFICATION_TYPES : [])], [supportMessagesEnabled,forumAvailable])
  const [forumTarget,setForumTarget] = useState(() => forumAvailable ? forumInitialTarget() : null)
  const companySnapshots = useMemo(() => createCompanySnapshotStore(user.id, {
    storage:isSupportObserver?null:getCompanySnapshotStorage(),sessionKey:user.last_sign_in_at,
  }), [user.id,user.last_sign_in_at,isSupportObserver])
  const [activeTab, setActiveTab] = useState(forumAvailable && forumInitialTarget() ? 'forum' : initialCompanyId ? 'home' : initialTab)
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId)
  const [visitedTabs, setVisitedTabs] = useState(() => ({
    home: !initialCompanyId && initialTab === 'home',
    swipe: !initialCompanyId && initialTab === 'swipe',
    profile: !initialCompanyId && initialTab === 'profile',
  }))
  const [companyProfileReturn, setCompanyProfileReturn] = useState(initialCompanyId ? { tab: 'home' } : null)
  const [userPlan, setUserPlan] = useState(isSupportObserver?'Support':'Starter')
  const [unreadCount, setUnreadCount] = useState(0)
  const [newCompanyCount, setNewCompanyCount] = useState(0)
  const [notificationCount, setNotificationCount] = useState(0)
  const [notificationRevision, setNotificationRevision] = useState(0)
  const [discoveryRequest, setDiscoveryRequest] = useState(null)
  const discoveryRequestRevision = useRef(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const [pendingCompletionCount, setPendingCompletionCount] = useState(0)
  const [sessionReady, setSessionReady] = useState(!ENFORCE_SINGLE_DEVICE_LOCK)
  const [showLangMenu, setShowLangMenu] = useState(false)
  const [directMessageCompanyId, setDirectMessageCompanyId] = useState(null)
  const [directMessageDraft, setDirectMessageDraft] = useState(null)
  const [activeMessageMatchId, setActiveMessageMatchId] = useState(null)
  const [messageConversation, setMessageConversation] = useState({userId:user.id,open:false})
  const [supportThreadId, setSupportThreadId] = useState(null)
  const dashboardOwnerRef = useRef(user.id)
  dashboardOwnerRef.current = user.id
  const onConversationOpenChange = useCallback(open => {
    if (dashboardOwnerRef.current !== user.id) return
    setMessageConversation(current => current.userId === user.id && current.open === (open === true)
      ? current : {userId:user.id,open:open === true})
  }, [user.id])
  const messageConversationOpen = activeTab === 'messages' && !selectedCompanyId && messageConversation.userId === user.id && messageConversation.open
  const countsLifetime = useRef({active:false,epoch:0})
  const countsRequest = useRef(0)
  const unreadCountRef = useRef(0)
  const activeTabRef = useRef(activeTab)
  const activeMessageMatchIdRef = useRef(null)
  const sessionTokenRef = useRef(null)
  const signingOutRef = useRef(false)
  const inactivityTimerRef = useRef(null)
  const dashboardScrollRef = useRef(null)
  const dashboardViewportRef = useRef(null)
  const dashboardContentRef = useRef(null)
  const dashboardNavigationRef = useRef(null)
  useConversationViewport({active:messageConversationOpen,identity:user.id,containerRef:dashboardViewportRef})
  const forumOpen = forumAvailable && activeTab === 'forum' && !selectedCompanyId
  useForumScrollClearance({active:forumOpen&&sessionReady,identity:user.id,scrollRef:dashboardScrollRef,navigationRef:dashboardNavigationRef})
  const runNavigation = useNavigationMotion({
    tab: activeTab, identity: user.id, contentRef: dashboardContentRef, viewportRef: dashboardScrollRef, navigationRef: dashboardNavigationRef,
    blocked: isNativeApp() || !sessionReady || Boolean(selectedCompanyId || showNotifications || messageConversationOpen),
  })

  const openSupportConversation = useCallback(threadId => {
    if (!supportMessagesEnabled || !threadId) return
    setShowNotifications(false)
    setSelectedCompanyId(null)
    setCompanyProfileReturn(null)
    setDirectMessageCompanyId(null)
    setDirectMessageDraft(null)
    setActiveMessageMatchId(null)
    setActiveTab('messages')
    setSupportThreadId(threadId)
  }, [supportMessagesEnabled])

  useEffect(() => { setSupportThreadId(null) }, [user.id])

  useEffect(() => {
    const lifetime = countsLifetime.current
    lifetime.active = true
    lifetime.epoch += 1
    return () => { lifetime.active = false; lifetime.epoch += 1 }
  }, [user.id])

  const captureCountScope = () => {
    const epoch = countsLifetime.current.epoch, owner = user.id
    return () => countsLifetime.current.active && countsLifetime.current.epoch === epoch && dashboardOwnerRef.current === owner && !signingOutRef.current
  }

  useEffect(() => {
    const invalidate = event => { if (event.detail?.reason !== 'poll') companySnapshots.invalidate() }
    const refreshEntitlements = () => window.dispatchEvent(new Event('hubbing:companies-changed'))
    // Capture listeners invalidate before screens start their refresh reads.
    window.addEventListener('hubbing:companies-changed', invalidate, true)
    window.addEventListener('hubbing:entitlements-changed', refreshEntitlements, true)
    return () => {
      window.removeEventListener('hubbing:companies-changed', invalidate, true)
      window.removeEventListener('hubbing:entitlements-changed', refreshEntitlements, true)
      companySnapshots.invalidate()
    }
  }, [companySnapshots])

  useEffect(() => {
    if (activeTab !== 'swipe' || selectedCompanyId) setDiscoveryRequest(null)
  }, [activeTab, selectedCompanyId])

  useEffect(() => {
    if (selectedCompanyId || !['home', 'swipe', 'profile'].includes(activeTab)) return
    setVisitedTabs(previous => previous[activeTab] ? previous : { ...previous, [activeTab]: true })
  }, [activeTab, selectedCompanyId])

  useEffect(() => {
    if (!sessionReady) return undefined
    return scheduleNavigationPreload(Object.values(navigationScreenLoaders))
  }, [sessionReady, user.id, isSupportObserver])

  useEffect(() => {
    if (!initialCompanyId) return
    const url = new URL(window.location.href)
    url.searchParams.delete('company')
    if (['login', 'register'].includes(url.searchParams.get('screen'))) url.searchParams.delete('screen')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
    onCompanyLinkOpened?.()
  }, [initialCompanyId, onCompanyLinkOpened])

  useEffect(() => {
    if (activeTab === 'pricing' && dashboardScrollRef.current) dashboardScrollRef.current.scrollTop = 0
  }, [activeTab])
  const ui = getUiText(lang)

  const updateNativeAppBadge = async (count) => {
    const nextCount = Math.max(0, Number(count) || 0)
    if (nextCount > 0) {
      await syncUnreadAppBadge(nextCount)
    } else {
      await clearAppBadge()
    }
    return nextCount
  }

  const releaseSessionLock = async () => {
    if (!isNativeApp() || !ENFORCE_SINGLE_DEVICE_LOCK) {
      sessionTokenRef.current = null
      return
    }

    const token = sessionTokenRef.current
    if (!token) return

    try {
      await supabase.rpc('hubbing_release_session_lock', { p_token: token })
    } catch (error) {
      console.warn('Unable to release session lock:', error)
    }
  }

  const forceSignOut = async (message) => {
    if (signingOutRef.current) return
    signingOutRef.current = true
    if (message) window.alert(message)
    await releaseSessionLock()
    await clearAppBadge()
    await signOutCurrentBrowser()
  }

  useEffect(() => {
    if (!sessionReady || isSupportObserver) return undefined
    return startCompanyDataSync(supabase, user.id)
  }, [sessionReady, user.id, isSupportObserver])

  useEffect(() => {
    unreadCountRef.current = unreadCount
  }, [unreadCount])

  useEffect(() => {
    activeTabRef.current = activeTab
    if (activeTab !== 'messages') setActiveMessageMatchId(null)
  }, [activeTab])

  useEffect(() => {
    activeMessageMatchIdRef.current = activeMessageMatchId
    if (sessionReady && activeMessageMatchId) loadNotificationCounts()
  }, [activeMessageMatchId, sessionReady])

  useEffect(() => {
    let cancelled = false

    if (!isNativeApp() || !ENFORCE_SINGLE_DEVICE_LOCK) {
      sessionTokenRef.current = null
      setSessionReady(true)
      return () => {
        cancelled = true
      }
    }

    const acquireSessionLock = async () => {
      const token = getStoredSessionToken(user.id)
      sessionTokenRef.current = token
      const { data, error } = await supabase.rpc('hubbing_acquire_session_lock', {
        p_token: token,
        p_device_label: getDeviceLabel(),
        p_ttl_minutes: SESSION_LOCK_TTL_MINUTES,
      })
      if (cancelled) return
      if (error) {
        console.warn('Session lock unavailable:', error)
        setSessionReady(true)
        return
      }
      if (data?.acquired === false) {
        console.warn('Session lock already active; allowing access to avoid blocking login:', data)
        if (ENFORCE_SINGLE_DEVICE_LOCK) {
          await forceSignOut(t.sessionAlreadyOpen)
          return
        }
        setSessionReady(true)
        return
      }
      setSessionReady(true)
    }

    acquireSessionLock()
    return () => {
      cancelled = true
    }
  }, [user.id, t.sessionAlreadyOpen])

  useEffect(() => {
    if (!sessionReady || !isNativeApp() || !ENFORCE_SINGLE_DEVICE_LOCK) return undefined

    const refreshSessionLock = async () => {
      const token = sessionTokenRef.current
      if (!token || signingOutRef.current) return
      const { data, error } = await supabase.rpc('hubbing_refresh_session_lock', {
        p_token: token,
        p_ttl_minutes: SESSION_LOCK_TTL_MINUTES,
      })
      if (error) {
        console.warn('Unable to refresh session lock:', error)
        return
      }
      if (data?.active === false) {
        console.warn('Session lock inactive; keeping current auth session active:', data)
        if (ENFORCE_SINGLE_DEVICE_LOCK) {
          await forceSignOut(t.sessionAlreadyOpen)
        }
      }
    }

    refreshSessionLock()
    const interval = window.setInterval(refreshSessionLock, 60 * 1000)
    const handleVisibilityChange = () => {
      if (!document.hidden) refreshSessionLock()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [sessionReady, t.sessionAlreadyOpen])

useEffect(() => {
  if (!sessionReady || !isNativeApp() || !ENABLE_INACTIVITY_SIGNOUT) return undefined

  const expireForInactivity = async () => {
    await forceSignOut(t.sessionExpired)
  }
  const resetInactivityTimer = () => {
    window.clearTimeout(inactivityTimerRef.current)
    inactivityTimerRef.current = window.setTimeout(expireForInactivity, SESSION_IDLE_LIMIT_MS)
  }
  const events = ['click', 'keydown', 'touchstart', 'scroll', 'pointerdown']
  events.forEach(eventName => window.addEventListener(eventName, resetInactivityTimer, { passive: true }))
  resetInactivityTimer()

  return () => {
    window.clearTimeout(inactivityTimerRef.current)
    events.forEach(eventName => window.removeEventListener(eventName, resetInactivityTimer))
  }
}, [sessionReady, t.sessionExpired])

useEffect(() => {
  if (!sessionReady || isSupportObserver) return undefined
  let cleanupPush = null
  let active = true
  let refreshTimer
  const owner = user.id
  const supportPush = createSupportNotificationRouter({
    onOpen: target => { if (active && dashboardOwnerRef.current === owner && !signingOutRef.current) openSupportConversation(target.threadId) },
    onRefresh: () => { if (active && dashboardOwnerRef.current === owner && !signingOutRef.current) loadNotificationCounts() },
  })
  const refreshAfterPush = (notification, meta = {}) => {
    if (!active || dashboardOwnerRef.current !== owner || signingOutRef.current) return
    const data = notification?.data || {}
    const notificationType = data.type || notification?.type
    if (notificationType === SUPPORT_NOTIFICATION_TYPE) {
      if (supportMessagesEnabled) supportPush(notification, meta)
      return
    }
    const opportunityCompanyId = data.companyId || data.opportunityCompanyId || notification?.companyId || notification?.opportunityCompanyId
    if (meta.action === 'performed' && notificationType === NEED_OPPORTUNITY_NOTIFICATION_TYPE && opportunityCompanyId) {
      setActiveTab('home')
      setCompanyProfileReturn({ tab: 'home' })
      setSelectedCompanyId(opportunityCompanyId)
      supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('type', NEED_OPPORTUNITY_NOTIFICATION_TYPE)
        .eq('opportunity_company_id', opportunityCompanyId)
        .eq('read', false)
        .then(({ error }) => {
          if (error) console.warn('Unable to mark opportunity notification as read:', error.message)
        })
    }
    window.clearTimeout(refreshTimer)
    refreshTimer = window.setTimeout(() => {
      if (active && dashboardOwnerRef.current === owner) loadNotificationCounts()
    }, 500)
  }
  registerPushNotifications({ onNotification: refreshAfterPush }).then(cleanup => {
    if (!active) {
      cleanup?.()
      return
    }
    cleanupPush = cleanup
  })
  return () => {
    active = false
    window.clearTimeout(refreshTimer)
    cleanupPush?.()
  }
}, [sessionReady, user.id, isSupportObserver, supportMessagesEnabled, openSupportConversation])

useEffect(() => {
  if (!sessionReady || isSupportObserver) return undefined
  let active = true
  loadNotificationCounts()
  const sub = supabase
    .channel('notifications-' + user.id)
    .on('postgres_changes', {
      event: '*', schema: 'public', table: 'notifications',
      filter: `user_id=eq.${user.id}`
    }, async (payload) => {
      if (!active || dashboardOwnerRef.current !== user.id) return
      const notification = payload.new || payload.old || {}
      if (!appNotificationTypes.includes(notification.type)) return

      const nextBadgeCount = await loadNotificationCounts()
      if (!active || dashboardOwnerRef.current !== user.id) return
      if (notification.type === 'new_company') {
        if (payload.eventType === 'INSERT') {
          window.dispatchEvent(new Event('hubbing:companies-changed'))
          if (activeTabRef.current === 'swipe' && notification.id) {
            const { error } = await supabase.from('notifications').update({ read: true })
              .eq('id', notification.id).eq('user_id', user.id).eq('type', 'new_company')
            if (!error) await loadNotificationCounts()
          }
        }
        return // New companies have an in-app badge, never a lock-screen alert.
      }
      if (payload.eventType !== 'INSERT' || notification.read) return
      // Support alerts are delivered once by the dedicated APNs/FCM worker.
      // The realtime event refreshes the bell and message badges only.
      if (notification.type === SUPPORT_NOTIFICATION_TYPE) return

      const activeMatchId = activeMessageMatchIdRef.current
      const isOpenConversationMessage =
        notification.type === 'new_message' &&
        activeTabRef.current === 'messages' &&
        activeMatchId &&
        notification.match_id === activeMatchId
      if (isOpenConversationMessage) return

      if (notification.type === 'new_message') {
        await showNativeNotification({
          title: t.notificationNewMessageTitle,
          body: t.notificationNewMessageBody,
          count: nextBadgeCount,
          id: `message-${notification.id || Date.now()}`,
        })
      } else if (notification.type === 'new_match') {
        await showNativeNotification({
          title: t.notificationNewMatchTitle,
          body: t.notificationNewMatchBody,
          count: nextBadgeCount,
          id: `match-${notification.id || Date.now()}`,
        })
      } else if (notification.type === NEED_COMPLETION_NOTIFICATION_TYPE) {
        await showNativeNotification({
          title: t.notificationNeedCompletionTitle,
          body: t.notificationNeedCompletionBody,
          count: nextBadgeCount,
          id: `need-completion-${notification.id || Date.now()}`,
        })
      } else if (notification.type === NEED_OPPORTUNITY_NOTIFICATION_TYPE) {
        await showNativeNotification({
          title: t.notificationNeedOpportunityTitle,
          body: t.notificationNeedOpportunityBody,
          count: nextBadgeCount,
          id: `need-opportunity-${notification.id || Date.now()}`,
        })
      } else if (notification.type === 'need_shared') {
        await showNativeNotification({
          ...needShareNotificationText(notification, lang),
          count: nextBadgeCount,
          id: `need-shared-${notification.id || Date.now()}`,
        })
      }
    })
    .subscribe()
  return () => { active = false; supabase.removeChannel(sub) }
}, [sessionReady, user.id, isSupportObserver, appNotificationTypes, lang, t.notificationNewMessageTitle, t.notificationNewMessageBody, t.notificationNewMatchTitle, t.notificationNewMatchBody, t.notificationNeedCompletionTitle, t.notificationNeedCompletionBody, t.notificationNeedOpportunityTitle, t.notificationNeedOpportunityBody])

const loadUnreadCount = async (current = captureCountScope()) => {
  if (isSupportObserver || !current()) return 0
  let query = supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .in('type', messageNotificationTypes)
    .eq('read', false)

  const activeMatchId = activeMessageMatchIdRef.current
  if (activeTabRef.current === 'messages' && activeMatchId) {
    query = query.or(`match_id.is.null,match_id.neq.${activeMatchId}`)
  }

  const { count } = await query
  if (!current()) return 0
  const nextCount = count || 0
  unreadCountRef.current = nextCount
  setUnreadCount(nextCount)
  return nextCount
}

const loadPendingCompletionCount = async (current = captureCountScope()) => {
  if (isSupportObserver || !current()) return 0
  const { data: myCompany, error } = await supabase
    .from('companies')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle()
  if (!current()) return 0
  if (error || !myCompany?.id) {
    setPendingCompletionCount(0)
    return 0
  }
  const nextCount = await fetchPendingNeedCompletionCount(myCompany.id)
  if (!current()) return 0
  setPendingCompletionCount(nextCount)
  return nextCount
}

const loadNewCompanyCount = async (current = captureCountScope()) => {
  if (isSupportObserver || !current()) return 0
  const { count, error } = await supabase.from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', user.id).eq('type', 'new_company').eq('read', false)
  if (!current()) return 0
  if (!error) setNewCompanyCount(count || 0)
  return count || 0
}

const loadNotificationCounts = async () => {
  if (isSupportObserver) return 0
  const scope = captureCountScope(), request = ++countsRequest.current
  const current = () => scope() && request === countsRequest.current
  if (!current()) return 0
  const [messageCount, completionCount] = await Promise.all([
    loadUnreadCount(current),
    loadPendingCompletionCount(current),
    loadNewCompanyCount(current),
  ])
  if (!current()) return 0
  let query = supabase.from('notifications').select('id', { count: 'exact', head: true }).eq('user_id', user.id).in('type', appNotificationTypes).eq('read', false)
  const activeMatchId = activeMessageMatchIdRef.current
  if (activeTabRef.current === 'messages' && activeMatchId) query = query.or(`match_id.is.null,match_id.neq.${activeMatchId}`)
  const { count, error } = await query
  if (!current()) return 0
  if (error) return (messageCount || 0) + (completionCount || 0)
  const totalCount = count || 0
  setNotificationCount(totalCount)
  setNotificationRevision(n => n + 1)
  await updateNativeAppBadge(totalCount)
  return totalCount
}

useEffect(() => {
  if (isSupportObserver || !sessionReady || activeTab !== 'swipe' || selectedCompanyId) return undefined
  let active = true
  supabase.from('notifications').update({ read: true })
    .eq('user_id', user.id).eq('type', 'new_company').eq('read', false)
    .then(({ error }) => {
      if (!active || error) return
      setNewCompanyCount(0)
      loadNotificationCounts()
    })
  return () => { active = false }
}, [sessionReady, activeTab, selectedCompanyId, user.id, isSupportObserver])

  useEffect(() => {
    if (!sessionReady || isSupportObserver) return undefined

    let refreshTimer = null
    const refreshUnreadCount = () => {
      if (document.hidden || signingOutRef.current) return
      window.clearTimeout(refreshTimer)
      refreshTimer = window.setTimeout(() => {
        loadNotificationCounts()
      }, 250)
    }

    window.addEventListener('focus', refreshUnreadCount)
    window.addEventListener('pageshow', refreshUnreadCount)
    document.addEventListener('visibilitychange', refreshUnreadCount)

    return () => {
      window.clearTimeout(refreshTimer)
      window.removeEventListener('focus', refreshUnreadCount)
      window.removeEventListener('pageshow', refreshUnreadCount)
      document.removeEventListener('visibilitychange', refreshUnreadCount)
    }
  }, [sessionReady, user.id, isSupportObserver])

  useEffect(() => {
    if (isSupportObserver) return
    const totalBadgeCount = notificationCount
    updateNativeAppBadge(totalBadgeCount)
  }, [notificationCount,isSupportObserver])

  useEffect(() => {
  if(isSupportObserver)return undefined
  let active = true
  const refreshPlan = () => loadActivePlan(user.id)
    .then(activePlan => {
      if (!active) return
      setUserPlan(activePlan.charAt(0).toUpperCase() + activePlan.slice(1))

      const requestedPlan = String(user.user_metadata?.requested_plan || 'starter').toLowerCase()
      const promptKey = `hubbing_requested_plan_shown_${user.id}_${requestedPlan}`
      let alreadyShown = false
      try {
        alreadyShown = window.localStorage.getItem(promptKey) === '1'
      } catch {
        // Storage is optional; the selected plan can still open normally.
      }
      if (activePlan === 'starter' && ['basic', 'premium'].includes(requestedPlan) && !alreadyShown) {
        try {
          window.localStorage.setItem(promptKey, '1')
        } catch {
          // Continue even if private browsing blocks local storage.
        }
        setActiveTab('pricing')
      }
    }).catch(error => console.warn('Unable to load plan:', error.message))
  const onVisible = () => { if (!document.hidden) refreshPlan() }
  refreshPlan()
  window.addEventListener('hubbing:entitlements-changed', refreshPlan)
  document.addEventListener('visibilitychange', onVisible)
  // Also enforce trial expiry while a screen remains open.
  const timer = window.setInterval(onVisible, 60000)
  return () => {
    active = false
    window.clearInterval(timer)
    window.removeEventListener('hubbing:entitlements-changed', refreshPlan)
    document.removeEventListener('visibilitychange', onVisible)
  }
  }, [user.id,isSupportObserver])
  const handleLogout = async () => {
    signingOutRef.current = true
    await releaseSessionLock()
    await clearAppBadge()
    await signOutCurrentBrowser()
  }

const handleTabChange = (tab) => runNavigation(tab, () => {
  setShowNotifications(false)
  setDiscoveryRequest(null)
  setActiveTab(tab)
  setSelectedCompanyId(null)
  setCompanyProfileReturn(null)
  if (tab !== 'messages') {
    setSupportThreadId(null)
    setActiveMessageMatchId(null)
    setDirectMessageCompanyId(null)
    setDirectMessageDraft(null)
  }
})

const openForum = (target = null) => {
  if(!forumAvailable)return
  setForumTarget({...target,revision:Date.now()})
  handleTabChange('forum')
}
const closeForum = () => {
  const url=new URL(window.location.href)
  url.searchParams.delete('forum');url.searchParams.delete('forumLang')
  window.history.replaceState({},'',url)
  setForumTarget(null);handleTabChange('home')
}

const handleCompanyProfileBack = () => {
  const returnTarget = companyProfileReturn
  setSelectedCompanyId(null)
  setCompanyProfileReturn(null)
  if (returnTarget?.tab) setActiveTab(returnTarget.tab)
  if (returnTarget?.tab === 'messages' && returnTarget.companyId) {
    setDirectMessageCompanyId(returnTarget.companyId)
    setDirectMessageDraft(null)
  }
}

  const tabStyle = (tab) => ({
    flex:1, padding:'8px 0 6px', background:'none', border:'none', cursor:'pointer',
    fontSize:11, color: activeTab === tab ? '#E24B4A' : '#6B7280',
    fontWeight: activeTab === tab ? 700 : 500,
    fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  })

  if (!sessionReady) {
    return (
      <LoadingIndicator fullScreen background="white" />
    )
  }

  return (
    <Suspense fallback={<LoadingIndicator fullScreen background="white" />}>
    <div ref={dashboardViewportRef} style={{
  height:'100dvh',
  display:'flex',
  flexDirection:'column',
  overflow:'hidden',
  background:'white'
}}>
      <div style={{
  padding:'calc(env(safe-area-inset-top) + 0.55rem) 1rem 0.55rem',
  borderBottom:'1px solid #f0f0f0',
  display:'flex',
  alignItems:'center',
  justifyContent:'space-between',
  flexShrink:0,
  background:'white',
  zIndex:20
}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div onClick={() => handleTabChange('home')} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
  <img src="/LOGO-HUBBING.svg" alt="Hubbing" style={{width:32,height:32,borderRadius:8}} />
  <span style={{fontWeight:700,fontSize:16}}>Hubbing</span>
</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{position:'relative'}}>
  <button onClick={() => setShowLangMenu(!showLangMenu)}
    style={{background:'#f5f5f5',border:'1px solid #eee',borderRadius:20,padding:'5px 10px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'}}>
    {lang.toUpperCase()} ▾
  </button>
  {showLangMenu && (
    <div style={{position:'absolute',right:0,top:'110%',background:'white',border:'1px solid #eee',borderRadius:12,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',overflow:'hidden',zIndex:100,minWidth:120}}>
      {[{code:'fr',label:'Français'},{code:'de',label:'Deutsch'},{code:'it',label:'Italiano'},{code:'en',label:'English'}].map(l => (
        <button key={l.code} onClick={() => { setLang(l.code); setShowLangMenu(false) }}
          style={{display:'block',width:'100%',padding:'8px 16px',background: lang === l.code ? '#FFF5F5' : 'white',border:'none',cursor:'pointer',fontSize:13,textAlign:'left',color: lang === l.code ? '#E24B4A' : '#333',fontWeight: lang === l.code ? 600 : 400}}>
          {l.label}
        </button>
      ))}
    </div>
  )}
</div>
          {isSupportObserver?<span style={{padding:'6px 10px',borderRadius:12,background:'#FFF7ED',color:'#9A3412',fontSize:12,fontWeight:700}}>Support</span>:<button onClick={() => handleTabChange('pricing')}
            aria-label={`${userPlan} — ${getBillingCopy(lang).changePlan}`} title={`${userPlan} — ${getBillingCopy(lang).changePlan}`}
            style={{background:'transparent',border:'none',borderRadius:12,padding:5,width:40,height:40,display:'grid',placeItems:'center',cursor:'pointer'}}>
            <PlanBadge plan={userPlan} />
          </button>}
          <button disabled={isSupportObserver} onClick={() => {if(!isSupportObserver)setShowNotifications(value => !value)}} aria-label={`Notifications${notificationCount ? `, ${notificationCount} non lues` : ''}`} aria-expanded={showNotifications} style={{position:'relative',background:'none',border:'none',cursor:'pointer',padding:8,color:'#475569',width:40,height:40}}>
            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {notificationCount > 0 && <span style={{position:'absolute',top:0,right:0,minWidth:17,height:17,padding:'0 3px',borderRadius:9,background:'#E24B4A',color:'white',fontSize:10,fontWeight:800,display:'grid',placeItems:'center',border:'1px solid white'}}>{notificationCount > 99 ? '99+' : notificationCount}</span>}
          </button>
        </div>
      </div>

      {isSupportObserver&&<div role="status" style={{padding:'8px 12px',background:'#FFF7ED',color:'#9A3412',fontSize:12,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}><span>Mode observation · Support Hubbing</span><button type="button" onClick={onOpenSupport} style={{border:'1px solid #FDBA74',borderRadius:8,padding:'6px 9px',background:'white',color:'#9A3412',fontWeight:700,cursor:'pointer'}}>Espace support</button></div>}

      <div ref={dashboardScrollRef} style={{
  flex:1,
  minHeight:0,
  display:'flex',
  flexDirection:'column',
  overflowY:messageConversationOpen ? 'hidden' : 'auto',
  overflowX:'hidden',
  WebkitOverflowScrolling:'touch',
  position:'relative',
  paddingBottom:messageConversationOpen ? 'var(--hubbing-chat-bottom-inset, env(safe-area-inset-bottom))' : forumOpen ? 'var(--hubbing-forum-navigation-height, calc(60px + env(safe-area-inset-bottom)))' : 'calc(60px + env(safe-area-inset-bottom))'
}}>
  <Suspense fallback={<LoadingIndicator fill />} >
  {!isSupportObserver && showNotifications && <NotificationCenter userId={user.id} types={appNotificationTypes} lang={lang} revision={notificationRevision} onClose={() => setShowNotifications(false)} onChange={loadNotificationCounts} onOpen={async notification => {
    setShowNotifications(false)
    if (forumAvailable && FORUM_NOTIFICATION_TYPES.includes(notification.type)) {
      openForum({topicId:notification.forum_topic_id,language:notification.forum_language||lang,moderation:notification.type==='forum_pending'})
      return
    }
    if (notification.type === SUPPORT_NOTIFICATION_TYPE) {
      openSupportConversation(supportThreadIdFromNotification(notification))
      return
    }
    if (notification.type === 'new_company') {
      handleTabChange('swipe')
      setDiscoveryRequest({
        companyId: notification.opportunity_company_id || notification.company_id || null,
        revision: ++discoveryRequestRevision.current,
      })
      return
    }
    if (notification.type === 'need_shared') { handleTabChange('profile'); return }
    if (notification.type === NEED_COMPLETION_NOTIFICATION_TYPE) { handleTabChange('profile'); return }
    if (notification.opportunity_company_id) {
      handleTabChange('home'); setCompanyProfileReturn({tab:'home'}); setSelectedCompanyId(notification.opportunity_company_id); return
    }
    if (notification.match_id) {
      const {data} = await supabase.from('matches').select('company_a(id,user_id),company_b(id,user_id)').eq('id', notification.match_id).maybeSingle()
      const other = data?.company_a?.user_id === user.id ? data.company_b : data?.company_a
      handleTabChange('messages')
      if (other?.id) setDirectMessageCompanyId(other.id)
    }
  }} />}
  {selectedCompanyId && (
    <CompanyProfileScreen
  companyId={selectedCompanyId}
  isAuthenticated={Boolean(user)}
  isSupportObserver={isSupportObserver}
  plan={userPlan}
  lang={lang}
  onBack={handleCompanyProfileBack}
  setActiveTab={setActiveTab}
  setSelectedCompanyId={setSelectedCompanyId}
  setCompanyProfileReturn={setCompanyProfileReturn}
  setDirectMessageCompanyId={setDirectMessageCompanyId}
  setDirectMessageDraft={setDirectMessageDraft}
/>
  )}
  <div ref={dashboardContentRef} style={{display:selectedCompanyId ? 'none' : 'contents'}}>
    <>
      {(visitedTabs.home || activeTab === 'home') && <div hidden={activeTab !== 'home'} style={{display:activeTab === 'home' ? 'contents' : 'none'}}>
        <HomeScreen key={user.id} onOpenForum={()=>openForum()} isSupportObserver={isSupportObserver} onReturnToSupport={onOpenSupport} user={user} companySnapshots={companySnapshots} isActive={activeTab === 'home' && !selectedCompanyId} setActiveTab={setActiveTab} setSelectedCompanyId={setSelectedCompanyId} plan={userPlan} lang={lang} />
      </div>}
      {(visitedTabs.swipe || activeTab === 'swipe') && <div hidden={activeTab !== 'swipe'} style={{display:activeTab === 'swipe' ? 'contents' : 'none'}}>
        <SwipeScreen
          key={user.id}
          user={user}
          isSupportObserver={isSupportObserver}
          companySnapshots={companySnapshots}
          isActive={activeTab === 'swipe' && !selectedCompanyId}
          plan={userPlan}
          setActiveTab={setActiveTab}
          setSelectedCompanyId={setSelectedCompanyId}
          setCompanyProfileReturn={setCompanyProfileReturn}
          setDirectMessageCompanyId={setDirectMessageCompanyId}
          setDirectMessageDraft={setDirectMessageDraft}
          discoveryRequest={discoveryRequest}
          lang={lang}
        />
      </div>}
      {!selectedCompanyId && activeTab === 'forum' && forumAvailable && <ForumScreen key={`${user.id}:${forumTarget?.revision||'initial'}`} user={user} lang={lang} initialTarget={forumTarget} onBack={closeForum} onNotificationChange={loadNotificationCounts}/>}
      {!selectedCompanyId && activeTab === 'map' && <MapScreen user={user} isSupportObserver={isSupportObserver} plan={userPlan} setSelectedCompanyId={setSelectedCompanyId} setCompanyProfileReturn={setCompanyProfileReturn} setActiveTab={setActiveTab} lang={lang} />}
      {!selectedCompanyId && activeTab === 'messages' && (isSupportObserver?<SupportObserverMessages user={user} lang={lang} isActive onOpenSupport={onOpenSupport} onConversationOpenChange={onConversationOpenChange}/>:<MessagesScreen user={user} companySnapshots={companySnapshots} plan={userPlan} setSelectedCompanyId={setSelectedCompanyId} setCompanyProfileReturn={setCompanyProfileReturn} setActiveTab={setActiveTab} supportThreadId={supportThreadId} onSupportTargetConsumed={threadId => setSupportThreadId(current => current === threadId ? null : current)} openMatchWithCompanyId={directMessageCompanyId} openMessageDraft={directMessageDraft} onDirectOpenHandled={() => { setDirectMessageCompanyId(null); setDirectMessageDraft(null) }} onUnreadChange={loadNotificationCounts} onActiveMatchChange={setActiveMessageMatchId} onConversationOpenChange={onConversationOpenChange} lang={lang} />)}
      {!selectedCompanyId && activeTab === 'pricing' && (isSupportObserver?<section style={{padding:24}}><h2>Compte Support Hubbing</h2><p>Le mode observation n’est associé à aucun abonnement entreprise.</p><button type="button" onClick={()=>handleTabChange('home')}>Retour à l’accueil</button><button type="button" onClick={onOpenSupport}>Espace support</button></section>:<PricingScreen user={user} setActiveTab={setActiveTab} lang={lang} />)}
      {(visitedTabs.profile || activeTab === 'profile') && <div hidden={activeTab !== 'profile'} style={{display:activeTab === 'profile' ? 'contents' : 'none'}}>
        <Activity mode={activeTab === 'profile' && !selectedCompanyId ? 'visible' : 'hidden'}>
          <ProfileScreen key={user.id} isSupportObserver={isSupportObserver} onReturnToSupport={onOpenSupport} user={user} isActive={activeTab === 'profile' && !selectedCompanyId} onLogout={handleLogout} setActiveTab={setActiveTab} plan={userPlan} lang={lang} onPendingCompletionChange={loadNotificationCounts} onOpenAdmin={onOpenAdmin} />
        </Activity>
      </div>}
    </>
  </div>
  </Suspense>
</div>

      <nav ref={dashboardNavigationRef} aria-label="Navigation principale" style={{
  position:'fixed',
  left:'50%',
  bottom:0,
  transform:'translateX(-50%)',
  width:'100%',
  maxWidth:430,
  borderTop:'1px solid #f0f0f0',
  display:messageConversationOpen ? 'none' : 'flex',
  background:'white',
  paddingBottom:'env(safe-area-inset-bottom)',
  flexShrink:0,
  zIndex:9999
}}>
  {[
    {id:'home',label:ui.nav.home,icon:'home'},
    {id:'swipe',label:ui.nav.swipe,icon:'swipe'},
    {id:'map',label:ui.nav.map,icon:'map'},
    {id:'messages',label:ui.nav.messages,icon:'messages'},
    {id:'profile',label:ui.nav.profile,icon:'profile'},
  ].map(tab => (
    <button key={tab.id} onClick={() => handleTabChange(tab.id)} aria-label={tab.id === 'swipe' && activeTab !== 'swipe' && newCompanyCount > 0 ? `${tab.label}, ${ui.nav.newCompanies(newCompanyCount)}` : undefined} style={tabStyle(tab.id)}>
      <div data-navigation-icon={tab.id} style={{position:'relative',height:26,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:2}}>
        <HubbingIcon name={tab.icon} active={activeTab === tab.id} />
        {tab.id === 'messages' && unreadCount > 0 && (
          <div style={{position:'absolute',top:-7,right:'calc(50% - 28px)',background:'#E24B4A',color:'white',borderRadius:999,minWidth:20,height:18,padding:'0 5px',fontSize:10,lineHeight:1,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid white',boxShadow:'0 2px 5px rgba(0,0,0,0.18)',boxSizing:'border-box'}}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
        {tab.id === 'swipe' && activeTab !== 'swipe' && newCompanyCount > 0 && (
          <div aria-hidden="true" style={{position:'absolute',top:-7,right:'calc(50% - 28px)',background:'#E24B4A',color:'white',borderRadius:999,minWidth:20,height:18,padding:'0 5px',fontSize:10,lineHeight:1,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid white',boxShadow:'0 2px 5px rgba(0,0,0,0.18)',boxSizing:'border-box'}}>
            {newCompanyCount > 9 ? '9+' : newCompanyCount}
          </div>
        )}
        {tab.id === 'profile' && pendingCompletionCount > 0 && (
          <div style={{position:'absolute',top:-7,right:'calc(50% - 28px)',background:'#E24B4A',color:'white',borderRadius:999,minWidth:20,height:18,padding:'0 5px',fontSize:10,lineHeight:1,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid white',boxShadow:'0 2px 5px rgba(0,0,0,0.18)',boxSizing:'border-box'}}>
            {pendingCompletionCount > 9 ? '9+' : pendingCompletionCount}
          </div>
        )}
      </div>
      <span style={{display:'block'}}>{tab.label}</span>
      <span style={{display:'block',width:4,height:4,borderRadius:'50%',background:activeTab === tab.id ? '#E24B4A' : 'transparent',margin:'4px auto 0'}} />
    </button>
  ))}
</nav>
    </div>
    </Suspense>
  )
}

